// ── Contest platform wire shapes (EX-PUB08) ─────────────────────────────────
// `docs/29_CONTEST_PLATFORM_SPEC.md` §10's two namespaces: `/v1/contests/*` is
// the contestant's (soft-authenticated) and `/v1/contest-platform/*` is the
// organizer's (authenticated, contest scope, no platform permission).
//
// **Nothing here re-derives who may do what.** Every payload the server sends
// carries a `viewer` or a `viewerScope` block, and the client renders off that
// block alone - `docs/29` §4.4 is a set of decisions a second copy of the rules
// in TypeScript would quietly disagree with, on exactly the surfaces where that
// is expensive.

import type { JudgeVerdict, RunTwiceSummary, StudentTestResult } from "@/features/quiz/coding-task-api";
import type { PlaybackFrame } from "@/features/quiz/types";
import type { ProblemDifficulty } from "@/features/judge/types";
import type { ProblemFileIo } from "@/features/judge/lib/file-io";
import type { FunctionSignature, FunctionStarters } from "@/features/judge/lib/function-signature";
import type { ContestAuthorshipReport } from "./lib/contest-authorship";
import type { RegistrationQuestion, ViewerRegistrationState } from "./registration";

export const CONTEST_PHASES = [
  "draft",
  "scheduled",
  "running",
  "frozen",
  "ended",
  "unfrozen",
] as const;
export type ContestPhase = (typeof CONTEST_PHASES)[number];

export const CONTEST_VISIBILITIES = ["public", "unlisted", "private"] as const;
export type ContestVisibility = (typeof CONTEST_VISIBILITIES)[number];

/**
 * Every door the server may send. `"linked"` (`EX-DAY04`) is a warm-up's: its
 * field is its main contest's, and only the warm-up create writes it, so no
 * form offers it ({@link CONTEST_SELECTABLE_REGISTRATIONS}).
 */
export const CONTEST_REGISTRATIONS = ["open", "passphrase", "invite", "course", "issued", "linked"] as const;
export type ContestRegistration = (typeof CONTEST_REGISTRATIONS)[number];
/** The five doors an organizer picks from in a form. */
export const CONTEST_SELECTABLE_REGISTRATIONS = ["open", "passphrase", "invite", "course", "issued"] as const;

export const CONTEST_PARTICIPATION_MODES = ["individual", "team"] as const;
export type ContestParticipationMode = (typeof CONTEST_PARTICIPATION_MODES)[number];

export const CONTEST_SCORING_FORMATS = ["icpc", "points", "codeforces"] as const;
export type ContestScoringFormat = (typeof CONTEST_SCORING_FORMATS)[number];

/**
 * `EX-RUL01`, `docs/29` §11 decision 17 - the contest's stance on AI
 * assistance, resolved server-side and never echoed raw: a platform-stamped
 * round always reads `not_allowed` here, whatever `Contest.aiPolicy` holds.
 */
export const CONTEST_AI_POLICIES = ["allowed", "assist_only", "not_allowed"] as const;
export type ContestAiPolicy = (typeof CONTEST_AI_POLICIES)[number];

/**
 * `EX-CFS01`, `docs/29` §6.3: the three knobs of the `codeforces` curve.
 *
 * Always present on the organizer payload, resolved to the seeds when the
 * contest carries none, so a form never has to guess what is in force.
 */
export type CodeforcesScoring = {
  decay: number;
  floorRatio: number;
  wrongPenalty: number;
};

export const CONTEST_PARTICIPANT_STATUSES = [
  "pending",
  "registered",
  "blocked",
  "disqualified",
] as const;
export type ContestParticipantStatus = (typeof CONTEST_PARTICIPANT_STATUSES)[number];

export type ContestTeamStatus = "active" | "disqualified";
export type ContestClarificationStatus = "pending" | "answered";
export type ContestClarificationVisibility = "asker" | "public";

export type ContestDirectoryScope = "upcoming" | "running" | "past";

/** Field limits mirrored from the models so a form refuses before the server does. */
export const CONTEST_TITLE_MAX = 200;
export const CONTEST_SLUG_MAX = 120;
/**
 * The description is `ProgsityEditor` HTML since 2026-09-18 (`docs/21` §12).
 * Markup costs characters that say nothing new, so the ceiling moved with the
 * format; the backend's `CONTEST_DESCRIPTION_MAX` is the same number.
 */
export const CONTEST_DESCRIPTION_MAX = 20_000;
export const CONTEST_PASSPHRASE_MIN = 4;
export const CONTEST_PASSPHRASE_MAX = 100;
export const CONTEST_PROBLEM_MAX = 26;
/** `EX-SFX02`: `GET .../problems/check` answers for at most this many ids (the backend's own bound). */
export const CONTEST_PROBLEM_CHECK_MAX = 50;
export const CONTEST_TEAM_NAME_MAX = 120;
/** `EX-SCB01`: the people on a team as the organizer typed them, coach included. */
export const CONTEST_TEAM_ROSTER_MAX = 10;
export const CONTEST_STANDINGS_MEMBER_MODES = ["names", "nicknames", "off"] as const;
export type ContestStandingsMemberMode = (typeof CONTEST_STANDINGS_MEMBER_MODES)[number];

/**
 * `EX-SCB01`, `docs/29` §11 decision 20: what a standings row shows beyond a
 * name. Resolved by the server, so it is never absent where it is sent; every
 * flag off is today's scoreboard. `seat` has a source since `EX-ONS01`: the
 * contest's seat plan, one `ContestSeat` per entry.
 */
export type StandingsDisplay = {
  institution: boolean;
  members: ContestStandingsMemberMode;
  logo: boolean;
  country: boolean;
  seat: boolean;
  /** `EX-IDN06`: what a person's row leads with; a rated round is always `username`. Absent from an older API. */
  label?: "name" | "username";
};

/**
 * `EX-SCB01`: who a row is, as much of it as the organizer flagged. Every key
 * is present only when flagged **and** known, so a team with the logo switch
 * on and no logo has no `logoImageId` at all: nothing is drawn there, not a
 * box, not a broken image (founder decision, 2026-09-11). `members` is the
 * people as names or nicknames, never a coach.
 */
export type StandingsRowIdentity = {
  institution?: string;
  logoImageId?: string;
  country?: string;
  /** `EX-IDN06`: ISO 3166-1 alpha-2, the flag, when the country is shown and the person has one. */
  countryCode?: string;
  /** `EX-IDN06`: the handle in its owner's capitals, when the row leads with it. */
  username?: string;
  members?: string[];
  /** `EX-ONS01`: the seat's label and its room, when the contest shows seats and the entry has a chair. */
  seat?: string;
  room?: string;
};
export const CONTEST_STATUS_REASON_MAX = 500;
export const CONTEST_CLARIFICATION_QUESTION_MAX = 2000;
export const CONTEST_CLARIFICATION_ANSWER_MAX = 4000;
export const CONTEST_ANNOUNCEMENT_TITLE_MAX = 200;
export const CONTEST_ANNOUNCEMENT_BODY_MAX = 5000;

/* ── The contestant's side (`/v1/contests/*`) ─────────────────────────────── */

export type PublicContestHost = {
  name: string;
  username: string | null;
  avatarImageId: string | null;
};

/**
 * `EX-HST03`, `docs/29` §5: the organization a contest is hosted by, printed
 * in place of the owner's name, or beside Progsity as co-host on a Progsity
 * round. Never an id.
 */
export type PublicContestHostOrganization = {
  name_en: string;
  name_bn: string | null;
  logoImageId: string | null;
};

export type PublicContestSummary = {
  _id: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  visibility: ContestVisibility;
  registration: ContestRegistration;
  participation: ContestParticipationMode;
  scoringFormat: ContestScoringFormat;
  /**
   * Whether this contest moves ratings (`EX-PUB07`, `docs/29` §7.1).
   *
   * On the directory card as well as the page, because the promotion is gated
   * before the start precisely so a contestant knows whether their rating is at
   * risk when they decide to compete.
   */
  rated: boolean;
  /** When the operator computed the ratings, or `null` if they have not yet. */
  ratingAppliedAt: string | null;
  /**
   * `EX-ADM03`, `docs/29` §9: a platform-funded official round. A fact about
   * who stands behind the event, shown as a badge beside `rated`; the funding
   * terms themselves never reach the public payload.
   */
  official: boolean;
  /**
   * `EX-PUB19`: who runs a community contest, the owner's public identity
   * (name, username, avatar; never an id); `null` on a Progsity round.
   * Optional, so a response from an API older than the field still parses.
   */
  host?: PublicContestHost | null;
  /**
   * `EX-HST03`: the host organization, preferred over `host` when present.
   * Optional, so a response from an API older than the field still parses.
   */
  hostOrganization?: PublicContestHostOrganization | null;
  /** `EX-RUL01`. Shown before registering, on the directory card and the page alike. */
  aiPolicy: ContestAiPolicy;
  startAt: string | null;
  endAt: string | null;
  freezeAt: string | null;
  phase: ContestPhase;
  isOpen: boolean;
  /**
   * `EX-PUB16`: whether the field's submission list is open, which is the
   * server's own predicate rather than "ended and not frozen" re-derived here.
   * Optional, so a client meeting an older backend simply offers nothing.
   */
  submissionsRevealed?: boolean;
  /** `null` before the window opens - the count itself is withheld (`docs/29` §6). */
  problemCount: number | null;
  /**
   * `EX-PUB22`: every participant row, the page's own count, on the card too.
   * Optional: an older API sends it on the page only.
   */
  participantCount?: number;
  /** `EX-PUB22`: when the round was archived, on the card too. Optional for an older API. */
  archivedAt?: string | null;
  /**
   * This reader's own standing on the card (2026-09-26): `isRegistered` is the
   * page's predicate, a participant row of any status, so the card says
   * "Registered" exactly when the page it opens would. `false` for a stranger.
   * Optional: an older API sends no block and the card offers the door.
   */
  viewer?: { isRegistered: boolean; division?: ContestViewerDivision | null; round?: ContestViewerRound | null };
  /**
   * `EX-DIV01`, `docs/29` §8: who the round rates, `null` when Open (everyone
   * who takes part is rated). Optional: an older API sends no key.
   */
  division?: ContestDivision | null;
  /**
   * `EX-DIV04`, `docs/29` §8.1: the round this contest is a division of, or
   * `null`. The directory lists a round once, on its first division's row.
   * Optional: an older API sends no key.
   */
  round?: ContestRoundRef | null;
  /**
   * `EX-CPG01`: on a directory card, the first three authors (leads first)
   * and how many there are, for "by a, b and 2 more". The page carries the
   * whole list. Optional: an older API sends neither.
   */
  authors?: ContestAuthorCredit[];
  authorCount?: number;
};

/**
 * `EX-DIV01`: a contest's division, copied from a preset or custom. A band is
 * half-open: `ratedFrom` inclusive, `ratedBelow` exclusive, `null` for no limit.
 * `label` is `null` on a custom band the operator left unnamed.
 */
export type ContestDivision = {
  presetId: string | null;
  label: string | null;
  ratedFrom: number | null;
  ratedBelow: number | null;
  /** Somebody outside the band: takes part unofficially, or cannot register (Div. 1). */
  outsidePolicy: "unofficial" | "closed";
};

/**
 * `EX-DIV01`: where this signed-in reader stands against the division, on
 * their own contest page and directory card only (`null` signed out or
 * without a division). `rating` is 1200 with `newcomer` for somebody unrated.
 * `fixed` once the start decided; `registeredAs` is what their entry records,
 * and differing from `standing` is the "your rating crossed the band" notice.
 */
export type ContestViewerDivision = {
  standing: "rated" | "unofficial" | "closed";
  rating: number;
  newcomer: boolean;
  fixed: boolean;
  registeredAs: "rated" | "unofficial" | null;
};

/** `EX-DIV01`: one division chip of the directory, a preset in the admin order. */
export type ContestDivisionFilter = {
  id: string;
  label: string;
  ratedFrom: number | null;
  ratedBelow: number | null;
};

/**
 * `EX-DIV04`, `docs/29` §8.1: a round of 2 to 4 division contests in one
 * window, as the directory row and a division's own page name it, Div. 1
 * first. Each division is a whole contest with its own slug.
 */
export type ContestRoundRef = {
  slug: string;
  title_en: string;
  title_bn: string | null;
  divisions: Array<{ slug: string; label: string | null; ratedFrom: number | null; ratedBelow: number | null }>;
};

/**
 * `EX-DIV04`: on a round's directory row, the division this reader's rating
 * sends them to (`home`) and the one they hold a place in, by slug. `null`
 * signed out.
 */
export type ContestViewerRound = { home: string | null; registeredIn: string | null };

/** `EX-DIV04`: one division on the round page, a contest of its own. */
export type PublicRoundDivision = {
  slug: string;
  title_en: string;
  title_bn: string | null;
  phase: ContestPhase;
  scoringFormat: ContestScoringFormat;
  registration: ContestRegistration;
  division: ContestDivision | null;
  /** `null` until the problems are visible. */
  problemCount: number | null;
  participantCount: number;
};

/**
 * `EX-DIV04`: where a signed-in reader stands in the round. `home` is the
 * division whose band holds their rating (1200 with `newcomer` for somebody
 * unrated), `null` when that division is not shown to them. `entry` is the
 * place they hold, in any division; `registeredAs` differing from `standing`
 * before the start is the "your rating crossed the band" notice.
 */
export type PublicRoundViewer = {
  rating: number;
  newcomer: boolean;
  home: string | null;
  entry: {
    slug: string;
    status: ContestParticipantStatus;
    registeredAs: "rated" | "unofficial" | null;
    standing: "rated" | "unofficial" | "closed";
    fixed: boolean;
  } | null;
  canRegister: boolean;
  staffRole: ContestStaffRole | null;
};

/**
 * `EX-DIV04`, `GET /v1/contest-rounds/:slug`: the round page. One window, the
 * divisions this reader may see, and the letters table once every division's
 * problems are visible (`null` before). Never cached.
 */
export type PublicContestRound = {
  round: {
    slug: string;
    title_en: string;
    title_bn: string | null;
    official: boolean;
    startAt: string | null;
    endAt: string | null;
    freezeMinutes: number;
    freezeAt: string | null;
  };
  divisions: PublicRoundDivision[];
  participantCount: number;
  problems: Array<{
    title_en: string | null;
    title_bn: string | null;
    letters: Array<{ slug: string; letter: string }>;
  }> | null;
  viewer: PublicRoundViewer | null;
};

/** `EX-PUB22`: who runs it, the directory's second filter. */
export type ContestDirectoryKind = "official" | "community";

export type PublicContestListResponse = {
  items: PublicContestSummary[];
  scope: ContestDirectoryScope;
  /** `EX-PUB22`: what each tab would list under the same kind. Optional for an older API. */
  counts?: Record<ContestDirectoryScope, number>;
  /** `EX-DIV01`: the presets the division chips offer. Optional for an older API. */
  divisionFilters?: ContestDivisionFilter[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type ContestProblemIndexItem = {
  letter: string;
  title_en: string | null;
  title_bn: string | null;
  difficulty: ProblemDifficulty | null;
  /**
   * `EX-ARC01`: where to practise this problem, or `null`.
   *
   * Non-null exactly when the problem is public, so the link exists precisely
   * when its target does. A round whose every problem was refused therefore
   * carries no links, and the archive line renders off these rather than off
   * `archivedAt` alone.
   */
  practiceSlug: string | null;
  /** `EX-RTW03`: present only on a run-twice problem. */
  kind?: "run_twice" | "interactive";
  /** `EX-FNC05`: present only on a function problem. */
  answer?: "function";
};

/**
 * What the contest page is allowed to offer this reader.
 *
 * `isRegistered` and `isCompeting` are different questions after `EX-PUB06`:
 * a pending, blocked or disqualified contestant still has a row and still loads
 * this page, and a submit button rendered off the wrong one would be offered to
 * somebody the server will refuse.
 */
export type PublicContestViewer = {
  /** `EX-WMK01`: this competing caller's server-resolved identity, else null. */
  statementWatermark?: { name: string; username: string | null } | null;
  /** `EX-STP01`: this competing caller's decoy mark, else null. Absent on an older API. */
  statementDecoy?: { mark: string } | null;
  needsRulesAcceptance?: boolean;
  /**
   * `EX-REG01`: which of the organizer's required questions this participant
   * has not answered (one added after they registered) and whether they still
   * may. `null` when the contest asks nothing; absent on an older API.
   */
  registration?: ViewerRegistrationState;
  isAuthenticated: boolean;
  isRegistered: boolean;
  isCompeting: boolean;
  participantStatus: ContestParticipantStatus | null;
  canModerate: boolean;
  canRegister: boolean;
  /** `EX-STF01`: why the register button is gone, when it is. Staff never compete. */
  staffRole: ContestStaffRole | null;
  /**
   * `EX-OFF01`: the other reason: a Progsity operator account on a rated
   * contest. Optional on the wire until every backend serves it.
   */
  operatorAccount?: boolean;
  team: { _id: string; name: string; status: ContestTeamStatus } | null;
  /**
   * `EX-ONS01`: where this contestant sits, or `null`. Their own entry's seat
   * and nobody else's. Optional on the wire: an older backend sends nothing.
   */
  seat?: { label: string; room: string } | null;
  /**
   * `EX-ONS02`: whether the Print button exists on this viewer's workspace.
   * True only for somebody competing, inside an open window, on a contest
   * whose stamp carries the Onsite Kit. Optional on the wire: an older backend
   * sends nothing, which reads as no button.
   */
  canPrint?: boolean;
  /**
   * `EX-INT04`: what this contestant's own page is configured to watch, or
   * `null`.
   *
   * Materialises only for somebody who is competing, inside an open window, on a
   * contest that is watching. That is `docs/27` §5.1.10's player-versus-public
   * split applied to a contest: the profile is on the contest object for anyone
   * deciding whether to enter, and the axes are here for the person the sensors
   * would actually attach to.
   */
  integrity: ContestIntegrityAxes | null;
  /**
   * `EX-EDO01`: at the Editor only level, this competing reader's own
   * standing; `null` everywhere else. Optional on the wire until every backend
   * serves it.
   */
  editorOnly?: EditorOnlyStanding | null;
  /**
   * `EX-DAY04`: on a pack contest, its warm-up, for its registered entrants
   * and its staff once the warm-up is scheduled; `entered` says whether this
   * person is in it yet. `null` for everyone else. Optional on the wire until
   * every backend serves it.
   */
  warmUp?: ContestViewerWarmUp | null;
  /**
   * `EX-DAY06`: the certificate card, for somebody competing on a contest
   * that issues certificates: `waiting` until the board is final, then
   * `ready`. `null` for everyone else. Optional on the wire until every
   * backend serves it.
   */
  certificate?: { state: "waiting" | "ready" } | null;
  /**
   * `EX-DIV01`: this reader against the contest's division, `null` signed out
   * or on an Open contest. `canRegister` is already `false` for `closed`.
   * Optional on the wire until every backend serves it.
   */
  division?: ContestViewerDivision | null;
  /**
   * `EX-VRT01`, `docs/29` §6.22: on an archived round, whether this reader may
   * take it virtually (and why not), and their own try. `null` on every other
   * round; optional on the wire until every backend serves it.
   */
  virtual?: ContestViewerVirtual | null;
};

/** `EX-VRT01`: why a virtual try cannot start, the server's own words. */
export type ContestVirtualRefusal = "not_archived" | "staff" | "competed" | "taken" | "another_running";

/** `EX-VRT01`: one virtual try, as every surface shows it. */
export type ContestVirtualAttempt = {
  id: string;
  startedAt: string;
  endsAt: string;
  /** When it stops or stopped: `endsAt`, or Finish now when that came first. */
  endedAt: string;
  finishedEarly: boolean;
  lengthMinutes: number;
  running: boolean;
  minute: number;
  /** Written by the first read after the end; `null` until then. */
  result: {
    rank: number | null;
    fieldSize: number;
    solved: number;
    penaltyMinutes: number;
    score: number;
  } | null;
};

export type ContestViewerVirtual = {
  /** A new try may start now (signed out reads `true`: the page asks them to sign in). */
  available: boolean;
  reason: ContestVirtualRefusal | null;
  lengthMinutes: number;
  /** How many people have taken the round virtually. */
  taken: number;
  attempt: ContestVirtualAttempt | null;
};

/**
 * `EX-VRT01`: the real board at the try's minute, the reader's row placed in
 * it (`virtual: true` on that row). The scoreboard's own shape, so the board
 * draws it; no freeze, no rating columns.
 */
export type ContestVirtualStandingsResponse = ContestStandingsResponse & {
  virtual: { minute: number; running: boolean; endedAt: string };
  attempt: ContestVirtualAttempt;
};

/** `EX-VRT01`: the dashboard's card, the reader's own tries newest first. */
export type MyVirtualRoundsResponse = {
  items: {
    contest: { slug: string; title_en: string; title_bn: string | null; scoringFormat: ContestScoringFormat; problemCount: number };
    attempt: ContestVirtualAttempt;
  }[];
};

/** `EX-DAY04`: the main contest page's link to its warm-up. */
export type ContestViewerWarmUp = {
  slug: string;
  title_en: string;
  title_bn: string | null;
  startAt: string | null;
  endAt: string | null;
  entered: boolean;
};

/** `EX-DAY04`: a contest named by the other half of its pair. */
export type ContestWarmUpLink = { slug: string; title_en: string; title_bn: string | null };

/**
 * The stamped profiles, in rising order (ADR-0060 decision 1). `editor_only`
 * (`EX-EDO01`) is Focus where code is written only in Progsity's editor.
 */
export type ContestIntegrityProfile = "honour" | "focus" | "editor_only" | "invigilated";

/**
 * `EX-EDO01`: a competing contestant's **own** standing at the Editor only
 * level, from the contest view on load and from the presence call after. Their
 * leaves as the server counted them, the page's own time away, the
 * organizer's limit (`0` for none) and whether a staff member has to let them
 * back in.
 */
export type EditorOnlyStanding = {
  leaves: number;
  awayMs: number;
  limit: number;
  sinceRelease: number;
  held: boolean;
  heldUntil: string | null;
};

/** `EX-EDO01`: one contestant waiting to be let back in, for both consoles. */
export type ContestHoldRow = {
  participantId: string;
  userId: string;
  name: string | null;
  username: string | null;
  heldAt: string;
  heldUntil: string;
  leaves: number;
  awayMs: number;
};

export type ContestHoldsView = {
  editorOnly: boolean;
  leaveLimit: number;
  holdMinutes: number;
  holds: ContestHoldRow[];
};

/**
 * What this contest is watching, for the contestant it is watching.
 *
 * The narrowed axes: no `tabSwitch: "lock"`, no `fullscreen: "locked"` and no
 * `autoSubmitOnViolation`, because a profile never blanks a viewport, never
 * takes the Escape key and never submits for somebody. `screenCapture` is
 * stored and refused until `EX-INT05`.
 *
 * `authorship` is here rather than on the contest object because the founder's
 * rule is that the public payload carries the profile **and nothing else**:
 * which rounds meter a paste is not a fact an anonymous scraper is owed.
 */
export type ContestIntegrityAxes = {
  profile: ContestIntegrityProfile;
  tabSwitch: "off" | "tracked" | "warn";
  fullscreen: "off" | "required";
  clipboard: "off" | "block";
  graceCount: number;
  authorship: boolean;
  solveTimeAnomaly: boolean;
  workspaceSnapshots: boolean;
  screenCapture: { mode: "off" | "on_violation" | "interval"; intervalSec?: number };
  /**
   * `EX-EDO01`: leaves before a staff member lets the contestant back in, `0`
   * for none. Only the Editor only level holds a number; optional on the wire
   * because a backend before the field does not send it.
   */
  leaveLimit?: number;
};

/**
 * What a contest says it is, to anybody at all.
 *
 * One word, and it is the word the rules page shows before registration
 * (`EX-RUL01`), plus the one flag that changes what "nothing you type is read"
 * means (`EX-WSP01`). Everything else about the configuration is on the
 * viewer block.
 */
export type ContestIntegrityFlags = {
  profile: ContestIntegrityProfile;
  /** Optional: a backend before `EX-WSP01` does not send it; absent reads as off. */
  workspaceSnapshots?: boolean;
};

/** `EX-WSP01`: what the problem page posts. The exam's frame shape, one at a time. */
export type WorkspaceSnapshotBody = {
  clientSeq: number;
  kind: "full" | "diff";
  body: string;
  o?: number;
  d?: number;
  base?: number;
  /** `EX-EDR03`: this snapshot is the first to carry a file uploaded into the editor. */
  upload?: true;
};

/** `EX-WSP01`: the receipt. A bounded refusal is `accepted: false` with a reason, never an error. */
export type WorkspaceSnapshotReceipt = {
  accepted: boolean;
  seq?: number;
  /** The last seq the server holds for this entry's problem after the request. */
  head: number;
  reason?: "off" | "spacing" | "cap" | "rekey" | "size";
};

/** `EX-WSP01`: one frame of the organizer's replay, the exam's shape plus who wrote it. */
export type WorkspaceSnapshotFrame = PlaybackFrame & { u: string };

/** `EX-WSP01`: one entry's stream for one problem, as `EX-AUT02`'s replay reads it. */
export type WorkspaceSnapshotView = {
  contestId: string;
  participantId: string;
  problemId: string;
  letter: string | null;
  label: string;
  startedAt: string;
  firstAt: string;
  lastAt: string;
  frames: WorkspaceSnapshotFrame[];
  frameCount: number;
  insertedChars: number;
  spanSeconds: number;
  truncated: boolean;
  truncatedReason: "frames" | "size" | null;
  jumps: { index: number; t: number; chars: number; lines: number }[];
  writers: { userId: string; name: string | null }[];
};

export type PublicContestDetail = PublicContestSummary & {
  /** `EX-SPN01`: the sponsors and the page's look; absent from an older API. */
  brand?: PublicContestBrand | null;
  /**
   * `EX-INS01`: whether the page has an Institutions tab (the stamp and a
   * board that shows the institution; the table opens at the end), and the
   * published seasons this round is in. Absent from an older API.
   */
  institutionTable?: boolean;
  seasons?: ContestPageSeason[];
  description_en: string | null;
  description_bn: string | null;
  requiresApproval: boolean;
  /** `EX-ARC01`: when this round's problems entered the practice archive, or `null`. */
  archivedAt: string | null;
  participantCount: number;
  /** `null` before the window opens, for everybody who is not a moderator or staff. */
  problems: ContestProblemIndexItem[] | null;
  /** `EX-STF01`: the credit line, leads first (`docs/29` §4.1, §10). */
  authors: ContestAuthorCredit[];
  testers: ContestCredit[];
  /**
   * `EX-CPG01`, Wave 30 decision 2: who gets a certificate, from the console's
   * setup; `null` when the contest issues none. Optional for an older API.
   */
  certificates?: ContestPublicCertificates | null;
  /** `EX-CPG01`: the contest's own seat limit, `null` when it sets none. Optional on the wire. */
  seatLimit?: number | null;
  /** `EX-REG01`: the organizer's questions at the door. Optional on an older API. */
  registrationQuestions?: RegistrationQuestion[];
  viewer: PublicContestViewer;
  /**
   * `EX-INT04`: the stamped profile, and nothing else.
   *
   * Optional because a directory card does not carry it and a client built
   * before this deploy will not find it; absent reads as `honour`.
   */
  integrity?: ContestIntegrityFlags;
  /** `EX-SCB01`: what the standings tab shows beyond a name. Absent reads as all off. */
  standingsDisplay?: StandingsDisplay;
  /**
   * `EX-EDR05`: the owner's "Help for contestants" switch, read during the
   * round only (`guideAvailable`). Optional on the wire; absent reads as on.
   */
  submitGuide?: boolean;
  /** `EX-WMK01`: absent means off, including on an older API. */
  statementWatermark?: boolean;
  /** `EX-STP01`: select, copy, drag and right-click off on the statement during the round. Absent is off. */
  statementCopyGuard?: boolean;
  /** `EX-STP01`: the sample input keeps its Copy button under the guard. Absent is off. */
  statementSampleCopy?: boolean;
  /**
   * `EX-SCB05`: a signed-in reader may open any field program now (the list
   * is open and the organizer published the round), the predicate the field
   * program read enforces. Optional for an older API; absent reads as closed.
   */
  sourceOpen?: boolean;
  /**
   * `EX-SRC02`, ADR-0134: the organizer's switch, at any phase, so the page
   * tells the field before the end that every program opens then. Opens
   * nothing by itself; `sourceOpen` is the gate. Absent reads as off.
   */
  submissionsPublished?: boolean;
  /**
   * `EX-DAY04`: on a warm-up, the contest it warms up for, to a viewer who
   * could open that contest; `null` elsewhere. Optional on the wire.
   */
  warmUpOf?: ContestWarmUpLink | null;
  /**
   * `EX-PDF01`, decision 8: true when the round is public, listed, ended and
   * has a problem, so anyone signed in may download the set from this page.
   * Optional on the wire; absent reads as no button.
   */
  problemSetPdf?: boolean;
};

export type ContestRegisterResult = {
  registered: boolean;
  alreadyRegistered: boolean;
  status: ContestParticipantStatus;
  pendingApproval: boolean;
  slug: string;
  /** `EX-DIV01`: what the new entry records, `null` on a contest without a division. */
  division?: { standing: "rated" | "unofficial" } | null;
};

export type ContestProblemView = {
  letter: string;
  contestSlug: string;
  phase: ContestPhase;
  title_en: string;
  title_bn: string | null;
  statement_en: string;
  statement_bn: string | null;
  inputFormat_en: string | null;
  inputFormat_bn: string | null;
  outputFormat_en: string | null;
  outputFormat_bn: string | null;
  constraints_en: string | null;
  constraints_bn: string | null;
  notes_en: string | null;
  notes_bn: string | null;
  samples: {
    input: string;
    output: string;
    explanation_en: string | null;
    explanation_bn: string | null;
    /** `EX-RTW01`: a run-twice sample's example middle lines. */
    firstOutput?: string;
    secondInput?: string;
    /** `EX-IAC01`: an interactive sample's conversation. */
    interaction?: { from: "judge" | "program"; text: string }[];
  }[];
  limits: { timeMs: number; memoryKb: number };
  /** `EX-RTW01`: present only on a run-twice problem. */
  kind?: "run_twice" | "interactive";
  /** `EX-RTW05`: present only when the program reads or writes a file. */
  fileIo?: ProblemFileIo;
  /** `EX-FNC03`: a function problem's signature and the five starters (Wave 36 decision 3, every contest). */
  signature?: FunctionSignature;
  starters?: FunctionStarters;
  /** Revealed once the contest has ended (`docs/29` §6's post-contest reveal). */
  editorial: { editorial_en: string | null; editorial_bn: string | null } | null;
};

export type ContestSubmitInput = {
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
  /**
   * `EX-AUT06`: what was typed here and what arrived at once, in bytes and
   * counts (`docs/27` §9.6, `docs/29` §6.2).
   *
   * Optional on the wire and optional here, and sent **only** when the contest's
   * own payload says its profile enables it. Never content: the shape is window
   * offsets and paste sizes, and there is no field on it that could carry text.
   */
  authorship?: ContestAuthorshipReport;
};

/** 202 - queued in the contest band; the verdict arrives on the own-submissions read. */
export type ContestSubmitResult = {
  submissionId: string;
  letter: string;
  status: "queued" | "running" | "completed";
  queuePosition: number;
};

/**
 * The contestant's own row, under `count_only` (`docs/29` §6.1).
 *
 * No `testResults`: a per-test breakdown turns the judge into a debugger that
 * reveals the shape of the test data. `hidden` carries the pass count instead.
 */
export type ContestSubmissionItem = {
  submissionId: string;
  letter: string | null;
  /** `EX-PUB16`: optional, because the field arrived with that deploy. */
  languageKey?: string;
  languageVersion?: string;
  createdAt: string;
  status: "queued" | "running" | "completed";
  verdict?: JudgeVerdict;
  score: number;
  maxScore: number;
  timeMs?: number;
  memoryKb?: number;
  compileOutput?: string;
  testResults?: StudentTestResult[];
  hidden?: { passedCount: number; totalCount: number };
  /** `EX-RTW03`: a run-twice problem's row only; names no test. */
  runTwice?: RunTwiceSummary;
};

export type MyContestSubmissionsResponse = {
  phase: ContestPhase;
  isRegistered: boolean;
  isCompeting: boolean;
  participantStatus: ContestParticipantStatus | null;
  items: ContestSubmissionItem[];
  /** Every row matching the filter, not the page. `EX-PUB16`. */
  total: number;
  page?: number;
  limit?: number;
  totalPages?: number;
};

/**
 * The filter both submission lists take - `EX-PUB16`.
 *
 * Every member is optional here for the same reason it is optional on the wire:
 * the surfaces that only want "my recent submissions" send none of it, and they
 * are the ones that were shipped first.
 */
export type ContestSubmissionListQuery = {
  letter?: string;
  verdict?: JudgeVerdict;
  languageKey?: string;
  page?: number;
  limit?: number;
};

/**
 * `EX-SCB05`: the field list's filter, plus whose rows (one person, or one
 * team's members) for the standings popup on a published round. The own list
 * never takes these: it has no "whose".
 */
export type ContestFieldSubmissionListQuery = ContestSubmissionListQuery & {
  userId?: string;
  teamId?: string;
};

/**
 * One row of the field's list, once the round is over (`EX-PUB16`, ADR-0064
 * decision 9).
 *
 * No source, no per-test breakdown and no compile output, on purpose and
 * permanently: a round's problems enter the practice archive, so an accepted
 * program here would kill each archived problem on the day it arrives.
 */
export type ContestFieldSubmissionItem = {
  submissionId: string;
  userId: string;
  name: string | null;
  letter: string | null;
  languageKey: string;
  languageVersion: string;
  status: "queued" | "running" | "completed";
  verdict?: JudgeVerdict;
  score: number;
  maxScore: number;
  createdAt: string;
};

export type ContestFieldSubmissionsResponse = {
  phase: ContestPhase;
  canModerate: boolean;
  /**
   * `EX-SRC01`, ADR-0082 decision 3: the organizer published the round, so a
   * signed-in reader may open each row's program. Optional for an older API.
   */
  sourceOpen?: boolean;
  /**
   * `EX-HCK01`: the letters whose problem takes uphacks now. An accepted
   * program of one of them, once open, offers "Try to break it". Optional for
   * an older API.
   */
  uphackLetters?: string[];
  items: ContestFieldSubmissionItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

/** `EX-SRC01`: one program from a published round's field list. */
export type ContestFieldSubmissionSource = {
  submissionId: string;
  letter: string;
  name: string | null;
  languageKey: string;
  languageVersion: string | null;
  status: "queued" | "running" | "completed";
  verdict: JudgeVerdict | null;
  sourceCode: string;
  createdAt: string;
};

/**
 * `EX-SCB05`: one of the reader's own programs in a contest, opened from
 * their own row on the standings. The verdict's words come from the own list
 * (`count_only`); this read carries the program.
 */
export type ContestOwnSubmissionSource = Omit<ContestFieldSubmissionSource, "name">;

/**
 * `EX-SCB09`: one of the caller's own contest submissions from any contest,
 * for My Submissions' Contests tab (`GET /v1/me/contest-submissions`). The
 * contest page's own row, shaped by the same `count_only` policy (how many
 * tests passed, never which), plus where it was made. `letter` and the titles
 * are `null` for a contest or a problem that no longer exists.
 */
export type MyContestSubmissionEverywhere = Omit<ContestSubmissionItem, "testResults"> & {
  contestId: string;
  problemTitle_en: string | null;
  problemTitle_bn: string | null;
  balloonColor: { hex: string; name: string } | null;
};

/** A contest the caller has submitted in, for the tab's filter and its group heads. */
export type MyContestSubmissionsContest = {
  _id: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  phase: ContestPhase;
  startAt: string | null;
};

export type MyContestSubmissionsEverywhereResponse = {
  items: MyContestSubmissionEverywhere[];
  /** Every contest the caller has submitted in, newest start first. */
  contests: MyContestSubmissionsContest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type MyContestSubmissionsEverywhereQuery = {
  contestId?: string;
  verdict?: JudgeVerdict;
  page?: number;
  limit?: number;
};

export type ContestClarification = {
  _id: string;
  letter: string | null;
  question: string;
  status: ContestClarificationStatus;
  answer: string | null;
  answeredAt: string | null;
  visibility: ContestClarificationVisibility;
  createdAt: string;
  /** Separates the reply to your question from the broadcast everybody got. */
  mine: boolean;
};

export type ContestClarificationsResponse = {
  items: ContestClarification[];
  total: number;
};

export type ContestAnnouncement = {
  _id: string;
  title: string | null;
  body: string;
  postedAt: string;
  editedAt: string | null;
};

export type ContestAnnouncementsResponse = {
  items: ContestAnnouncement[];
  total: number;
};

export type StandingsCell = {
  letter: string;
  solved: boolean;
  /**
   * `EX-PUB15`: the first solve of this problem in the whole field, under
   * whatever cap this table was computed with.
   *
   * Optional, and absent reads as "not first": the field arrives with the
   * backend deploy and this client may be the older half of the pair for a few
   * minutes (rule 12, and `AGENTS.md` §6 rule 12's other direction). A client
   * that assumed it was present would paint every cell a first solve.
   */
  firstSolve?: boolean;
  attempts: number;
  solvedAtMinute: number | null;
  penaltyMinutes: number | null;
  score: number;
  maxScore: number;
  /** Hidden by the freeze, or simply not judged yet. */
  pending: number;
};

/**
 * One row's ledger entry (`EX-PUB07`, `docs/29` §7.3).
 *
 * `null` on every row until the operator applies the rating, and on team rows
 * always - a team contest is never rated (§7.4).
 *
 * `wasUnrated` rather than a null `before`: the ledger genuinely records 1200 as
 * the rating the contest started from, and "unrated" is what a *display* prints
 * for somebody's first contest. One field carries the difference so a client
 * does not infer it from a coincidence with the default.
 */
export type StandingsRowRating = {
  before: number;
  after: number;
  delta: number;
  seed: number;
  wasUnrated: boolean;
};

export type StandingsRow = {
  /**
   * `EX-DIV01`: `null` exactly on an unofficial row, which keeps its sorted
   * place; positions count official rows only (`docs/29` §8).
   */
  rank: number | null;
  /** `EX-DIV01`: somebody outside the division, on a board with one. Absent otherwise. */
  unofficial?: true;
  /** `EX-VRT01`: the reader's own row on their virtual board. Absent on every real board. */
  virtual?: true;
  entryId: string;
  entryType: "user" | "team";
  solved: number;
  penaltyMinutes: number;
  score: number;
  maxScore: number;
  lastImprovementMinute: number | null;
  pending: number;
  cells: StandingsCell[];
  name: string | null;
  members: { userId: string; name: string | null }[] | null;
  rating: StandingsRowRating | null;
  /**
   * `EX-PUB23` (ADR-0081 decision 2): the rating this person holds today, for
   * the band colour on their name; `null` when unrated and on every team row.
   * Optional, and absent reads as unrated, for the deploy-order reason
   * `firstSolve` gives.
   */
  currentRating?: number | null;
  /**
   * `EX-SCB01`. Optional, and absent reads as today's row: a client meeting an
   * older backend, or a contest whose organizer never opened the setting, draws
   * exactly the name and the members line it always drew.
   */
  identity?: StandingsRowIdentity;
};

/**
 * `EX-DAY05`, `docs/29` §6.11: one row of the reveal's board. The scoreboard's
 * own row without who it is (that is `ContestReveal.entries`, once per entry)
 * and without a rating (the rating follows the unfreeze).
 */
export type RevealRow = Pick<
  StandingsRow,
  "rank" | "entryId" | "entryType" | "solved" | "penaltyMinutes" | "score" | "maxScore" | "lastImprovementMinute" | "pending" | "cells"
>;

/** `EX-DAY05`: one press. The server decided it; the page only plays it. */
export type RevealStep = {
  entryId: string;
  /** Its position before the press, 0 the top. */
  from: number;
  /** And after it. */
  to: number;
  /** Its final row, carrying the rank it holds after this press. */
  row: RevealRow;
  /** Every rank this press changed. */
  ranks: { entryId: string; rank: number | null }[];
};

/** `EX-DAY05`: who an entry is, exactly as the scoreboard prints it. */
export type RevealEntry = Pick<StandingsRow, "entryId" | "entryType" | "name" | "members" | "identity">;

/** `GET /v1/contest-platform/contests/:id/reveal` (`EX-DAY05`). */
export type ContestReveal = {
  contest: {
    _id: string;
    slug: string;
    title_en: string;
    title_bn: string | null;
    scoringFormat: ContestScoringFormat;
    participation: ContestParticipationMode;
    startAt: string | null;
    endAt: string | null;
    freezeAt: string | null;
    rated: boolean;
  };
  display: StandingsDisplay;
  /**
   * `EX-SCB06`: each column carries the balloon colour the organizer set, the
   * scoreboard's own value, `null` where none. Optional, and absent reads as
   * no balloon, for a page that meets a backend from before the field.
   */
  problems: { letter: string; maxScore: number | null; balloonColor?: { hex: string; name: string } | null }[];
  entries: RevealEntry[];
  /** The frozen board as contestants see it, in its order. */
  frozen: RevealRow[];
  steps: RevealStep[];
  /** The final order with its ranks: where the last press lands. `EX-DIV01`: `null` and `unofficial` on an unofficial row. */
  final: { entryId: string; rank: number | null; unofficial?: true }[];
  /** The final table, hashed; compared again before the last press. */
  digest: string;
};

/**
 * Which board a standings read asks for. `auto` is the reader's own view (a
 * moderator sees through the freeze), `frozen` is the capped board on request,
 * and `public` is the board a contestant sees right now whoever asks: the
 * venue's Big screen sends it so a projector never shows the live order during
 * the freeze (`docs/29` §6.1).
 */
export type StandingsView = "auto" | "frozen" | "public";

export type ContestStandingsResponse = {
  contest: {
    slug: string;
    title_en: string;
    phase: ContestPhase;
    scoringFormat: ContestScoringFormat;
    startAt: string | null;
    endAt: string | null;
    freezeAt: string | null;
  };
  /** Whether this table is capped; `cap` is where. */
  frozen: boolean;
  cap: string | null;
  participation: ContestParticipationMode;
  /**
   * Two facts, because they mean different things to a reader: an empty delta
   * column on a rated contest awaiting its apply is not the same as one on a
   * contest that never moves ratings at all.
   */
  rating: { rated: boolean; applied: boolean; appliedAt: string | null };
  /**
   * The columns. `maxScore` is the `codeforces` value and `null` in the other
   * formats; the two counts are `EX-PUB15`'s, entries rather than submissions,
   * and they come out of the same capped fold as the cells, so a frozen table's
   * header never reports a solve the table is withholding. Optional for the same
   * deploy-order reason as `firstSolve`.
   */
  problems: {
    letter: string;
    maxScore: number | null;
    solvedCount?: number;
    attemptedCount?: number;
    /**
     * Entries with a submission still waiting for its verdict (or, on a frozen
     * board, hidden by the freeze), same unit and same cap as the two counts.
     * Optional for the deploy-order reason as `firstSolve`.
     */
    pendingCount?: number;
    /**
     * The colour the organizer set for this problem's balloon (`EX-ONS03`,
     * `docs/29` §6.2). The header draws a balloon above the letter only when it
     * is here; absent or `null` reads as no balloon.
     */
    balloonColor?: { hex: string; name: string } | null;
  }[];
  rows: StandingsRow[];
  /**
   * `EX-DIV01`: the division the board's tag names, `null` when Open, and how
   * many unofficial rows the field holds, shown or not (`?unofficial=show`).
   * Optional: an older API sends neither.
   */
  division?: ContestDivision | null;
  unofficialCount?: number;
  /**
   * `EX-SCB01`: what the rows show beyond a name, resolved, so the kiosk (which
   * holds nothing but this payload) knows which lines to draw. Optional for the
   * deploy-order reason as `firstSolve`; absent reads as all off.
   */
  display?: StandingsDisplay;
  /**
   * `EX-SCB01`: every institution in the field, only when the institution is
   * shown; `EX-ONS01`: every room of the seat plan, only when seats are shown.
   */
  segments?: { institutions?: string[]; rooms?: { id: string; name: string }[] };
  /**
   * Which row is the reader's - computed by the server, team contests included.
   *
   * `EX-PUB15` added where they stand and which page says so, both `null` for
   * somebody the table has no row for, and both optional because a client can
   * meet a backend that predates them.
   */
  you: {
    entryId: string | null;
    entryType: "user" | "team";
    rank?: number | null;
    page?: number | null;
  } | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

/* ── The organizer's side (`/v1/contest-platform/*`) ──────────────────────── */

export type ContestUserSummary = {
  _id: string;
  name: string;
  email: string;
  role: string;
  /** The public handle, `null` until the user has picked one. */
  username: string | null;
};

/** §4.1's columns, serialized so a client renders the buttons the service allows. */
export type ContestViewerScope = {
  isOwner: boolean;
  isPlatformOperator: boolean;
  canManageSettings: boolean;
  /**
   * `EX-HST03`: whether the host organization card is offered: an operator
   * on any contest, the owner on a pack contest or one linked to an
   * organization. Optional on the wire: an older backend sends nothing.
   */
  canSetHostOrganization?: boolean;
  canModerate: boolean;
  /** `EX-STF01`: the three staff facts, from the predicates the scope layer reads. */
  isLeadAuthor: boolean;
  isAuthor: boolean;
  isTester: boolean;
  /** `staff_read`: the pool and the ordered set. */
  canReadStaff: boolean;
  /** `EX-ONS03`: on the volunteer list. Optional on the wire: an older backend sends nothing. */
  isVolunteer?: boolean;
  /** `volunteer`: the print station and the balloon board. Optional for the same reason. */
  canRunOnsite?: boolean;
};

/* ── `EX-STF01`: staff and the pool (`docs/29` §4.1, §4.2, §10) ──────────── */

/**
 * One of the round's setters. `lead` is scope, not a second list: a lead
 * author moderates through the flag and stops at `own`, and clearing the flag
 * ends the right on the next request.
 */
export type ContestAuthorEntry = ContestUserSummary & {
  lead: boolean;
  addedBy: string | null;
  addedAt: string | null;
};

export type ContestTesterEntry = ContestUserSummary & {
  addedBy: string | null;
  addedAt: string | null;
  /**
   * `EX-DAY03`: the Progsity person a confirmed rehearsal put on the staff.
   * The owner does not remove them here; cancelling the rehearsal does.
   * Absent on every tester the owner named.
   */
  viaBooking?: boolean;
};

/** `{ userId }` or `{ username }`, exactly one. The console sends the handle. */
export type ContestPersonInput = { userId: string } | { username: string };

/* ── `EX-COL07`: contest staff by invitation (`docs/07`, ADR-0122) ─────────── */

/**
 * The four staff lists an invitation joins. The lead author is an author with
 * `lead: true`, not a fifth list; the ownership offer is its own thing
 * ({@link ContestStaffInvitation} with `role: "owner"`).
 */
export type ContestStaffListRole = Exclude<ContestStaffRole, "owner">;

/**
 * Who a staff add names, exactly one way (the server's bodies are `.strict()`).
 * The organizer's box sends `userId` for a person the picker found and `email`
 * for an address; a leading `@` on a `username` is ignored by the server.
 */
export type ContestStaffInput = { userId: string } | { username: string } | { email: string };

/** A person in an invitation row: never an id the owner has to type, `email` only for an operator. */
export type ContestInvitationPerson = {
  id: string;
  name: string;
  username?: string;
  usernameDisplay?: string;
  avatarImageId?: string;
  email?: string;
};

/**
 * An open (or just answered) staff invitation as the owner reads it. `role:
 * "owner"` is the ownership offer. Nobody is on a list, and the owner is
 * unchanged, until the person accepts.
 */
export type ContestStaffInvitation = {
  id: string;
  role: ContestStaffRole;
  /** A lead author's invitation; absent means false. */
  lead?: true;
  status: string;
  invitee: { user: ContestInvitationPerson } | { email: string };
  /** `null` when an operator invited and the viewer is not one: the web prints "Progsity". */
  invitedBy: ContestInvitationPerson | null;
  byOperator: boolean;
  createdAt: string;
  expiresAt: string;
};

/** The add, the ownership offer and the cancel all answer the detail as it now stands, plus the invitation. */
export type ContestStaffInviteResult = {
  contest: OrganizerContestDetail;
  /** `null` when the owner named themselves as Author and joined at once (`EX-CPG01`, ADR-0128). */
  invitation: ContestStaffInvitation | null;
};

/** What the contest page credits: name, handle and avatar, never a user id. */
export type ContestCredit = {
  name: string;
  username: string | null;
  avatarImageId: string | null;
  /** `EX-IDN02`, `EX-IDN03`: the handle in its owner's capitals, and where they study. Optional on the wire. */
  usernameDisplay?: string | null;
  institution?: string | null;
  /** `EX-CPG01`: today's rating, for the band colour on the handle; `null` is unrated. Optional on the wire. */
  rating?: number | null;
};

export type ContestAuthorCredit = ContestCredit & { lead: boolean };

/** `EX-CPG01`: what the page promises about certificates (`docs/29` §6.12), never the issuer. */
export type ContestPublicCertificates = {
  participation: boolean;
  /** The top N get the rank line; `null` means no rank line. */
  rankTopN: number | null;
};

/** Which of the contest's five lists names the viewer, or `null` (`docs/29` §5.1). */
export type ContestStaffRole = "owner" | "moderator" | "author" | "tester" | "volunteer";

/** One offer in the pool, as `GET .../pool` returns it. */
export type ContestPoolItem = {
  problemId: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  difficulty: ProblemDifficulty;
  visibility: string;
  offeredBy: { _id: string; name: string; username: string | null };
  offeredAt: string | null;
  /** In the set right now, and as which letter. The offer outlives a mount. */
  mounted: boolean;
  letter: string | null;
  /** The reference-solution gate as it stands, so the lead sees what will mount. */
  referenceSolution: {
    kind: "verified" | "absent" | "refused";
    status: "unverified" | "passed" | "failed" | null;
    stale: boolean;
  } | null;
};

export type ContestPoolResponse = {
  items: ContestPoolItem[];
  total: number;
  viewer: { canOffer: boolean };
};

/** One row of `GET /v1/contest-platform/authoring`: a round the caller is an author of. */
export type AuthoredRound = {
  contest: OrganizerContest;
  myRole: "lead" | "author";
  /** `draft` or `scheduled`: an offer is still possible. */
  canOffer: boolean;
  poolCount: number;
  offers: { problemId: string; offeredAt: string | null; mounted: boolean; letter: string | null }[];
};

export type AuthoringResponse = { items: AuthoredRound[]; total: number };

/** The pack's terms as sold, frozen onto the contest at schedule (`EX-PRC01`). */
export type ContestEventPackTermsView = {
  priceBdt: number;
  priceUsd: number;
  includedParticipants: number;
  overagePricePer100Bdt: number;
  overagePricePer100Usd: number;
  participantCap: number;
  validityDays: number;
  featuredListing: boolean;
  ratedPromotionRequestEligible: boolean;
  prioritySupport: boolean;
};

export type ContestEventPackStamp = {
  addonId: string;
  consumedAt: string;
  participantAllowance: number;
  terms: ContestEventPackTermsView;
};

/** The kit's terms as sold, frozen onto the contest at schedule (`EX-PKG02`). */
export type ContestOnsiteKitTermsView = {
  priceBdt: number;
  priceUsd: number;
  validityDays: number;
};

/**
 * `EX-PKG02`, ADR-0060 decision 10: the Onsite Kit spent on this contest.
 *
 * Beside an Event Pack rather than instead of one, and it admits nobody, so it
 * carries no `participantAllowance`.
 */
export type ContestOnsiteKitStamp = {
  addonId: string;
  consumedAt: string;
  terms: ContestOnsiteKitTermsView;
};

/** One unconsumed kit the caller may spend beside a pack (`EX-PKG02`). */
export type OrganizerOnsiteKit = {
  addonId: string;
  purchasedAt: string;
  expiresAt: string | null;
  usageRemaining: number;
  terms: ContestOnsiteKitTermsView;
};

/**
 * `EX-PKG02`, ADR-0060 decision 9: what this contest may do, resolved once at
 * schedule and never re-read.
 *
 * `null` on every contest scheduled before the stamp existed, and the console
 * reads that as today's behaviour rather than as "nothing allowed". Organizer
 * payload only: a contestant is told what is watching them on the rules page
 * (`EX-RUL01`), never by reading what the organizer bought.
 */
export type ContestCapabilitiesStamp = {
  profiles: ContestIntegrityProfile[];
  timingReport: "summary" | "full";
  integrityReport: boolean;
  workspaceSnapshots: boolean;
  tempAccounts: boolean;
  onsiteKit: boolean;
  /**
   * `EX-GAT01` (ADR-0070): teams this contest may hold, `-1` for no cap. Free
   * keeps twenty. Optional on the wire until every backend serves it; the
   * console reads an absent field as no cap, the server's own default.
   */
  maxTeams?: number;
  /** `EX-GAT01`: `summary` is the per-problem counts; `full` adds the groups themselves. */
  similarityReport?: "summary" | "full";
  /**
   * `EX-GAT03` (ADR-0089): entries this hall holds because its tools came from
   * the owner's Exam Pro, teams in a team contest; -1 otherwise. Optional on
   * the wire; absent reads -1.
   */
  maxHallEntries?: number;
  /** `EX-RPT01`: the Round Report's summary and PDF. Optional on the wire; absent reads false. */
  writtenReport?: boolean;
  /** `EX-PKG03`: the live overlay and the sponsor slots. Optional on the wire; absent reads false. */
  liveOverlay?: boolean;
  /** `EX-PKG03`: the ICPC Contest API feed. */
  icpcFeed?: boolean;
  /** `EX-PKG03`: the round's institution table. */
  institutionTable?: boolean;
  /** `EX-PKG03`: the talent pass. */
  talentPass?: boolean;
  /**
   * `EX-CLA01`: the Clarification Assistant's uses are included. Absent reads
   * true on a `championship` or `platform` stamp, as the server reads it.
   */
  clarificationAssistant?: boolean;
  source:
    | "plan"
    | "event_pack"
    | "onsite_kit"
    | "championship"
    | "org_allowance"
    | "bundle"
    | "platform"
    | "admin_grant";
  stampedAt: string;
};

/** `EX-PKG03`: the three add-ons a pack contest can spend, each named after its stamp field. */
export const CONTEST_STAMP_ADDON_ITEMS = ["liveOverlay", "writtenReport", "icpcFeed"] as const;
export type ContestStampAddonItem = (typeof CONTEST_STAMP_ADDON_ITEMS)[number];
/** Decision 8: the one a pack contest still adds alone; every pack carries the other two. */
export const SOLD_STAMP_ADDON_ITEMS: readonly ContestStampAddonItem[] = ["icpcFeed"];

/** `EX-PKG03`: the caller's unspent add-ons and setter credits (`GET /v1/contest-platform/pack-addons`). */
export type PackAddonHoldings = {
  addons: { addonId: string; item: ContestStampAddonItem; purchasedAt: string; expiresAt: string | null }[];
  setterAiCredits: number;
};

/** `EX-PKG03`: the setters' AI credits on one contest. `available` is the owner's own; null for anyone else. */
export type SetterAiCreditsView = {
  available: number | null;
  setters: { userId: string; name: string | null; username: string | null; role: "author" | "tester" }[];
  given: { userId: string; amount: number; at: string }[];
};

/** One unconsumed pack the caller may spend at schedule (`EX-PRC01`). */
export type OrganizerEventPack = {
  addonId: string;
  purchasedAt: string;
  expiresAt: string | null;
  usageRemaining: number;
  terms: ContestEventPackTermsView;
  /** Set when the pack was bought as the onsite contest, with a kit beside it (`EX-PAY03`). */
  soldAs?: "contest_onsite_event" | "contest_championship";
};

export type RatedRequestDecision = "promoted" | "declined";

/** What the settings panel's "Request rated" card renders (`docs/29` §7.1). */
export type OrganizerRatedRequest = {
  requestedAt: string;
  requestedBy: string;
  reason: string;
  decidedAt: string | null;
  decision: RatedRequestDecision | null;
  decidedBy: string | null;
  note: string | null;
};

/** The bounds the route's schema enforces, refused in the field rather than sent. */
export const RATED_REQUEST_REASON_MIN = 10;
export const RATED_REQUEST_REASON_MAX = 2000;

/* ── The hall's network guide (`EX-HAL01`, `docs/29` §6.18) ─────────────── */

/** `none` never asked, `requested` waiting for Progsity, `accepted` the guide is open. */
export type HallGuideState = "none" | "requested" | "accepted";
/** What each address is for: the site, its apex or `www` twin, the API, the image host. */
export type HallHostRole = "site" | "siteAlias" | "api" | "images";
export type HallHost = { host: string; role: HallHostRole };

export type ContestHallGuide = {
  state: HallGuideState;
  /** Why a request cannot be made now; `null` when it can, or one exists. The server's own answer. */
  blocked: "needs_onsite" | "needs_schedule" | "no_window" | "ended" | "warm_up" | null;
  requestedAt: string | null;
  note: string | null;
  acceptedAt: string | null;
  /** Progsity's messages, oldest first. */
  replies: { at: string; text: string }[];
  /** Present once accepted, made from the live config at the read. */
  guide: {
    hosts: HallHost[];
    signIn: ("google" | "github")[];
    testUrl: { en: string; bn: string };
    lockFrom: string;
    lockUntil: string;
    generatedAt: string;
  } | null;
};

/** The route's bound on the organizer's note, refused in the field rather than sent. */
export const HALL_GUIDE_NOTE_MAX = 1000;

/* ── The venue lock and the room code (`EX-VEN01`, `docs/29` §6.21) ────────── */

export type VenueNetwork = { name: string; cidr: string; addedAt?: string };

/** `GET .../venue`: the lock as the console edits it. Absent on the server reads as both off. */
export type ContestVenueLock = {
  /** Why the lock cannot be set here; `null` when it can. The server's own answer. */
  blocked: "needs_onsite" | "warm_up" | null;
  ended: boolean;
  networkOn: boolean;
  networks: VenueNetwork[];
  roomCodeOn: boolean;
  /** How many logins this contest issued: who the lock reads. */
  issuedLogins: number;
  updatedAt: string | null;
};

export type ContestVenueLockInput = { networkOn: boolean; networks: VenueNetwork[]; roomCodeOn: boolean };

/** `GET .../venue/my-network`: the organizer's own address and the listed network it is on. */
export type ContestVenueMyNetwork = {
  address: string | null;
  suggested: string | null;
  refusal: "private" | "unreadable" | null;
  matched: string | null;
};

/** `GET .../venue/room-code`: what the projector shows, derived on the server. */
export type ContestVenueRoomCode = {
  on: boolean;
  title: string;
  code: string | null;
  secondsLeft: number | null;
  windowSeconds: number;
};

export type VenueRefusalReason = "outside_venue" | "room_code_wrong" | "room_code_expired" | "room_code_too_many";
export type VenueRefusalWhere = "sign_in" | "submit" | "run";

/** `GET .../venue/refusals`: the counts, the latest refusals and the logins let in. Never an address. */
export type ContestVenueRefusals = {
  total: number;
  byReason: Record<VenueRefusalReason, number>;
  recent: { userId: string; username: string; reason: VenueRefusalReason; where: VenueRefusalWhere; at: string; letIn: boolean }[];
  letIn: { userId: string; username: string; at: string }[];
};

export type OrganizerContest = {
  _id: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  description_en: string | null;
  description_bn: string | null;
  ownerId: string;
  ownerOrgId: string | null;
  moderatorCount: number;
  visibility: ContestVisibility;
  registration: ContestRegistration;
  participation: ContestParticipationMode;
  status: ContestPhase;
  startAt: string | null;
  endAt: string | null;
  freezeMinutes: number;
  freezeAt: string | null;
  unfrozenAt: string | null;
  scoringFormat: ContestScoringFormat;
  /** `EX-CFS01`: the `codeforces` curve, frozen at the start with the format. */
  codeforcesScoring: CodeforcesScoring;
  /** `EX-RUL01`. Resolved, editable from `draft`/`scheduled` only. */
  aiPolicy: ContestAiPolicy;
  /**
   * `EX-INT04`: the resolved integrity profile and its axes, editable from
   * `draft`/`scheduled` only. Never absent on this payload, because an unset
   * block resolves to `honour` rather than to an empty form.
   */
  integrity: ContestIntegrityAxes;
  /**
   * `EX-SCB01`: resolved by the server, so an unset block is every flag off
   * rather than an empty form. Optional until every backend serves it.
   */
  standingsDisplay?: StandingsDisplay;
  /**
   * `EX-ONS02`: resolved by the server, so an unset pair is the seeded limits
   * rather than an empty form. Optional until every backend serves it.
   */
  printLimits?: PrintLimits;
  /** `EX-ONS03`: whether a solve after the freeze minute still raises a balloon. Optional until every backend serves it. */
  balloonsDuringFreeze?: boolean;
  /** `EX-EDR05`: the "Help for contestants" switch, resolved by the server (absent is on). Optional for an older API. */
  submitGuide?: boolean;
  statementWatermark?: boolean;
  /** `EX-STP01`: the three statement switches, resolved by the server. Optional for an older API. */
  statementCopyGuard?: boolean;
  statementSampleCopy?: boolean;
  statementDecoy?: boolean;
  /** `EX-GAT03`: the organizer says this contest runs in a hall. Settled at schedule. Optional for an older API. */
  inHall?: boolean;
  /** `EX-SRC01`, ADR-0082 decision 3: the round's programs are published (at any phase since `EX-SRC02`, a promise until the list opens). Optional for an older API. */
  submissionsPublished?: boolean;
  /** `EX-PUB07`. Only a `contest.manage` holder can change either of these. */
  rated: boolean;
  ratingAppliedAt: string | null;
  /**
   * `EX-RAT01`, `docs/29` §7.5: the effective delay the scheduled apply waits
   * after the unfreeze, and when it will next act on this contest.
   *
   * `ratingAutoApplyAt` is `null` whenever nothing is scheduled: not rated, not
   * unfrozen, or already applied. Both are reads here. The delay is written from
   * the admin panel's Rating tab, on the operator table with the other three
   * rating writes (§7.1).
   */
  ratingDelayMinutes: number;
  ratingAutoApplyAt: string | null;
  /**
   * `EX-RRQ01`, `docs/29` §7.1: the owner's standing ask that this round be
   * rated, and the operator's answer. `null` until somebody asks.
   *
   * The owner writes and withdraws it from their own settings panel; the answer
   * is an operator's, and `note` is the note behind a decline. `decidedAt`
   * absent is the only thing that means open, on purpose: there is no `status`
   * field that could disagree with it (ADR-0057).
   *
   * Never on the public contest payload.
   */
  ratedRequest: OrganizerRatedRequest | null;
  courseId: string | null;
  requiresApproval: boolean;
  /**
   * `EX-PRC01` — the Event Pack this contest was scheduled on, or `null`.
   *
   * Present means the contest holds its own slot rather than one of the owner's
   * plan quota, admits `participantAllowance` registrants regardless of what the
   * owner's plan says today, and will refund the pack if it is unscheduled
   * before it starts. `terms` is the immutable snapshot it was sold at;
   * `participantAllowance` is the live number, raised by an admin when an
   * overage invoice is settled.
   */
  eventPack: ContestEventPackStamp | null;
  /** `EX-PKG02`: the Onsite Kit spent on this contest, if the organizer named one. */
  onsiteKit: ContestOnsiteKitStamp | null;
  /** `EX-PKG02`: what this contest may do. `null` on contests scheduled before the stamp. */
  capabilities: ContestCapabilitiesStamp | null;
  problemCount: number;
  phase: ContestPhase;
  isOpen: boolean;
  standingsFrozen: boolean;
  /**
   * `EX-PUB23`, `docs/29` §6.6: when the one reminder that the board is still
   * frozen went to the owner and the moderators, `null` until it has. Optional:
   * an older API sends nothing, and the console then leaves the line out.
   */
  frozenBoardReminder?: { sentAt: string } | null;
  createdAt: string;
  updatedAt: string;
  viewerScope: ContestViewerScope;
};

export type OrganizerContestDetail = OrganizerContest & {
  /** `EX-PKG03`: the add-ons spent on this pack contest; absent or null is none. */
  packAddons?: { item: ContestStampAddonItem; consumedAt: string }[] | null;
  owner: ContestUserSummary | null;
  moderators: (ContestUserSummary & { addedBy: string | null; addedAt: string | null })[];
  /** `EX-STF01`: the Staff tab's two lists and the pool's size. */
  authors: ContestAuthorEntry[];
  testers: ContestTesterEntry[];
  /** `EX-ONS03`: the runners, the third list. Optional on the wire: an older backend sends nothing. */
  volunteers?: ContestTesterEntry[];
  /**
   * `EX-COL07`: the open staff invitations, newest first, and the open
   * ownership offer. Sent only to a viewer holding `own` (the owner, or an
   * operator); absent for a moderator or a lead author, who see the lists
   * without the people who have not accepted yet.
   */
  pendingInvitations?: ContestStaffInvitation[];
  ownershipOffer?: { invitation: ContestStaffInvitation } | null;
  poolCount: number;
  participantCount: number;
  /** Whether a passphrase is set, never what it is. */
  hasPassphrase: boolean;
  /** `kind` (`EX-RTW03`) only on a run-twice problem. */
  problems: { letter: string; problemId: string; addedAt: string | null; kind?: "run_twice" | "interactive" }[];
  /**
   * `EX-ORG02`: whether the schedule control may offer the institution's
   * allowance, and what it should say about the cap if it does. `null` on a
   * contest not bound to an organization. Optional on the wire: an older
   * backend sends nothing.
   */
  orgFunding?: OrganizerOrgFundingOffer | null;
  /**
   * `EX-ADM03`, `docs/29` §9: the platform's funding stamp, set at create by a
   * `contest.manage` holder and never removed. Present on a Progsity round,
   * which the server refuses a pack, a kit or an allowance on (409,
   * `platform_funded`), so the schedule step offers none. `null` or absent on
   * every other contest. Optional on the wire: an older backend sends nothing.
   */
  platformFunding?: { stampedBy: string; stampedAt: string } | null;
  /**
   * The console rail (2026-09-18): the number the registration door refuses
   * on and where it comes from, resolved by the server in the door's own order.
   * `-1` is no cap. Optional on the wire until every backend serves it.
   */
  registrationCap?: { limit: number; source: RegistrationCapSource } | null;
  /** EX-SEAT01: null means no override on a legacy contest. */
  seatLimit?: number | null;
  /** `EX-HST03`: the host organization as saved; `organizationId` is the server's stamp. */
  hostOrganization?: (PublicContestHostOrganization & { organizationId: string | null }) | null;
  /** `EX-HST03`: the linked organization's name and logo, the card's one-click prefill. */
  hostOrganizationSuggestion?: { name_en: string; logoImageId: string | null } | null;
  registrationAllowance?: { limit: number; source: RegistrationCapSource } | null;
  /**
   * The console rail: on a draft, what a plain Schedule would stamp from the
   * owner's live plan, so a locked rail entry is the server's answer. `null`
   * once stamped, on a scheduled contest that predates stamps, or when the
   * owner's plan could not be read. Optional on the wire for the same reason.
   */
  capabilityPreview?:
    | (Omit<ContestCapabilitiesStamp, "stampedAt"> & {
        /**
         * `EX-GAT03`: the owner's hall. A positive number offers the "runs in
         * a hall" switch and a kit spent without a pack; 0 is a plan with no
         * hall, -1 a plan whose kit is on every contest.
         */
        hallPlanCap?: number;
      })
    | null;
  /**
   * `EX-GAT01`: how many teams the contest holds, for the Teams meter and the
   * schedule readiness against the stamp's allowance. Zero on an individual
   * contest. Optional on the wire until every backend serves it.
   */
  teamCount?: number;
  /**
   * `EX-DAY02`: the person on call, on a contest whose Event Pack was sold
   * with one, from scheduling on. `null` on every other contest. Optional on
   * the wire until every backend serves it.
   */
  dayContact?: ContestDayContactView | null;
  /**
   * `EX-DAY03`: the rehearsal with the Progsity team, on every contest an
   * Event Pack funds (`status: "none"` until the owner books), `null` on the
   * rest. Optional on the wire until every backend serves it.
   */
  rehearsalBooking?: ContestRehearsalBookingView | null;
  /**
   * `EX-DAY04`: on a pack contest, its warm-up (whether one can be created,
   * the one that exists, the bounds); `null` on every other contest. Optional
   * on the wire until every backend serves it.
   */
  warmUp?: ContestWarmUpView | null;
  /**
   * `EX-DAY04`: on a warm-up, its main contest; `null` on every other contest.
   * `platform` (`EX-OFF01`) when that is a Progsity round.
   */
  warmUpOf?: (ContestWarmUpSummary & { platform?: boolean }) | null;
  /**
   * `EX-DAY05`: whether this contest offers the reveal on the projector, the
   * server's one rule (a contest an Event Pack funds). When it opens is
   * `phase === "ended"` with `standingsFrozen`. Optional on the wire until
   * every backend serves it; absent reads as not offered.
   */
  revealOffered?: boolean;
  /**
   * `EX-DAY06`: the certificates with the organizer's logo, on a contest an
   * Event Pack funds (the settings, when they were fixed, how many were
   * issued, whether the board is final). `null` on every other contest.
   * Optional on the wire until every backend serves it.
   */
  certificates?: ContestCertificatesView | null;
};

/** `EX-DAY06`: one signature on the certificate. */
export type ContestCertificateSignatory = { name: string; title: string; imageId: string };

/** `EX-DAY06`: what the owner saves; the whole document on every save. */
export type ContestCertificateSettingsInput = {
  enabled: boolean;
  issuerName: string;
  logoImageId: string | null;
  signatories: ContestCertificateSignatory[];
  participation: boolean;
  rankTopN: number | null;
};

/** `EX-DAY06`: the organizer payload's `certificates`. */
export type ContestCertificatesView = {
  /**
   * `EX-OFF01`: who the form starts from as the presenter before anything is
   * saved, "Progsity" on a Progsity round; `null` elsewhere. Optional on the
   * wire until every backend serves it.
   */
  issuerDefault?: string | null;
  /** `EX-HST03`: the logo the form starts from, the host organization's; `null` elsewhere. */
  logoDefault?: string | null;
  settings: (ContestCertificateSettingsInput & { updatedAt: string }) | null;
  /** Fixed from the first certificate on. */
  lockedAt: string | null;
  issuedCount: number;
  boardFinal: boolean;
  bounds: {
    issuerMax: number;
    signatoriesMax: number;
    signatoryNameMax: number;
    signatoryTitleMax: number;
    rankTopNMax: number;
    rankTopNDefault: number;
  };
};

/** `EX-DAY06`: why there are no certificates yet, or `ready`. */
export type ContestCertificateState = "ready" | "no_event_pack" | "certificates_off" | "board_not_final" | "still_judging";

/** `EX-DAY06`: one certificate the reader may download. */
export type ContestCertificateRecipient = {
  recipientId: string;
  entryId: string;
  name: string;
  teamName: string | null;
  institution: string | null;
  kind: "participation" | "rank";
  rank: number | null;
  /** The rank line's words ("Champion", "4th place"), as the certificate prints them. */
  placement: string | null;
  /** `null` until it is issued (its first download, or the delivery at the final board). */
  certificate: {
    certificateId: string;
    verificationUrl: string;
    issuedAt: string;
    /** `EX-CRT01`: the one email to its holder; `null` until it is claimed. */
    email?: ContestCertificateEmailState | null;
  } | null;
};

/** `EX-CRT01`: how a certificate's email went. */
export type ContestCertificateEmailState = {
  outcome: "claimed" | "sent" | "skipped" | "failed";
  reason: "no_account" | "temp_account" | "no_email" | "send_failed" | null;
  at: string;
};

/** `EX-CRT01`: `POST /v1/contest-platform/contests/:id/certificates/email`. */
export type ContestCertificateEmailResult =
  | {
      scope: "all";
      issued: number;
      sent: number;
      skipped: number;
      failed: number;
      alreadyEmailed: number;
      /** With `resend`: left alone because one was sent to them in the last ten minutes. */
      recentlyEmailed?: number;
    }
  | { scope: "one"; certificateId: string; outcome: "sent" | "skipped" | "failed"; reason: string | null };

/** `EX-DAY06`: `GET /v1/contests/:slug/certificates`. */
export type ContestCertificateList = {
  state: ContestCertificateState;
  pending: number;
  canModerate: boolean;
  issuerName: string | null;
  recipients: ContestCertificateRecipient[];
};

/** `EX-DAY04`: one half of a warm-up pair, as the console names it. */
export type ContestWarmUpSummary = {
  _id: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  status: ContestPhase;
  startAt: string | null;
  endAt: string | null;
};

/** Why a pack contest cannot have a warm-up created now. */
export type ContestWarmUpRefusal = "warm_up_of_warm_up" | "no_event_pack" | "contest_started" | "warm_up_exists";

export type ContestWarmUpView = {
  contest: ContestWarmUpSummary | null;
  canCreate: boolean;
  refusal: ContestWarmUpRefusal | null;
  /** The founder's numbers (2026-09-28), served so the form's words carry them. */
  bounds: { maxMinutes: number; minGapMinutes: number; maxLeadDays: number };
  /** `EX-OFF01`: the main contest is a Progsity round, which spends no pack. Optional on the wire. */
  platform?: boolean;
};

/** `EX-DAY03`: where the owner's rehearsal booking stands. */
export type ContestRehearsalBookingStatus = "none" | "requested" | "confirmed" | "cancelled";

export type ContestRehearsalBookingView = {
  status: ContestRehearsalBookingStatus;
  requestedAt: string | null;
  /** The time the owner asked for. */
  requestedStartAt: string | null;
  /** The confirmed time, `null` until confirmed; another than the asked one when moved. */
  startAt: string | null;
  /** Who from Progsity runs it, by name only. */
  personName: string | null;
  /** That person's account is on the staff as a tester now. */
  testerOnStaff: boolean;
  cancelReason: "organizer" | "operator" | "unscheduled" | null;
  /** The bounds a new time must fall in, `null` when none is left before the start. */
  earliestStartAt: string | null;
  latestStartAt: string | null;
  slotMinutes: number;
};

/** `EX-DAY02`: the Progsity support line an Event Pack contest's organizer reaches. */
export type ContestDayContact = {
  /** Printed as written, title included. */
  name: string;
  /** As people read and dial it, e.g. "+880 1844-287496". */
  phone: string;
  /** Digits only, country code first: the `wa.me` form. */
  whatsapp: string;
  email: string;
};

export type ContestDayContactView = {
  /** The line as admin holds it now, read live by the server. */
  contact: ContestDayContact;
  /** Whoever an operator named for this contest's day; `null` until someone is. */
  onDutyName: string | null;
  /** An hour before the start. The cover runs until the results are out. */
  coversFrom: string | null;
};

export type RegistrationCapSource = "platform" | "event_pack" | "organization_plan" | "owner_plan" | "hall" | "seat_limit";

export type OrganizerOrgFundingOffer = {
  orgId: string;
  orgName: string;
  available: boolean;
  remaining: number;
  /** The organization's own cap, which an org-funded contest reads instead of the owner's. `-1` is no cap. */
  maxContestRegistrations: number;
};

export type OrganizerContestListResponse = {
  items: OrganizerContest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type CreateContestInput = {
  seatLimit?: number;
  title_en: string;
  title_bn?: string;
  description_en?: string;
  description_bn?: string;
  slug?: string;
  visibility?: ContestVisibility;
  registration?: ContestRegistration;
  participation?: ContestParticipationMode;
};

/** `EX-EXT01`: the minutes given back, why, whether contestants are told, and the retry key. */
export type ExtendContestInput = {
  minutes: number;
  reason: string;
  announce: boolean;
  requestKey: string;
};

export type UpdateContestInput = {
  seatLimit?: number;
  /** `EX-HST03`: `null` clears it. */
  hostOrganization?: { name_en: string; name_bn?: string | null; logoImageId?: string | null } | null;
  title_en?: string;
  title_bn?: string | null;
  description_en?: string | null;
  description_bn?: string | null;
  slug?: string;
  visibility?: ContestVisibility;
  registration?: ContestRegistration;
  participation?: ContestParticipationMode;
  startAt?: string | null;
  endAt?: string | null;
  freezeMinutes?: number;
  scoringFormat?: ContestScoringFormat;
  codeforcesScoring?: CodeforcesScoring;
  passphrase?: string | null;
  courseId?: string | null;
  requiresApproval?: boolean;
  aiPolicy?: ContestAiPolicy;
  /**
   * `EX-INT04`: the integrity profile and its axes, partial.
   *
   * The profile is the organizer's pick and the axes are its dials, so a body
   * carrying `{ profile: "focus" }` means the focus profile rather than "focus
   * with every dial reset to a default the caller did not send".
   */
  integrity?: Partial<Omit<ContestIntegrityAxes, "screenCapture">> & {
    screenCapture?: Partial<ContestIntegrityAxes["screenCapture"]>;
  };
  /** `EX-SCB01`: partial, merged over the resolved block. Never frozen with the window. */
  standingsDisplay?: Partial<StandingsDisplay>;
  /** `EX-ONS02`: partial, merged over the resolved pair. Refused without the Onsite Kit. */
  printLimits?: Partial<PrintLimits>;
  /** `EX-ONS03`: a solve after the freeze minute still raises a balloon. Never frozen with the window. */
  balloonsDuringFreeze?: boolean;
  /** `EX-EDR05`: "Help for contestants". Never frozen with the window; the server writes only a change. */
  submitGuide?: boolean;
  statementWatermark?: boolean;
  /** `EX-STP01`: every plan, editable during the round; the server writes only a change. */
  statementCopyGuard?: boolean;
  statementSampleCopy?: boolean;
  statementDecoy?: boolean;
  /** `EX-GAT03`: "This contest runs in a hall". Refused after scheduling when it would change. */
  inHall?: boolean;
};

/** `EX-STP01`: one mark found in a pasted text, and whose page it came from. */
export type StatementDecoyTraceEntry = {
  userId: string;
  name: string | null;
  username: string | null;
  teamId: string | null;
  teamName: string | null;
};

export type StatementDecoyTrace = {
  marks: { mark: string; entry: StatementDecoyTraceEntry | null }[];
};

/* ── The balloons (`EX-ONS03`, `docs/29` §6.2) ───────────────────────────── */

/** A problem's balloon colour: the hex the board draws, the name the runner says. */
export type ContestBalloonColor = { hex: string; name: string };

export const CONTEST_BALLOON_HEX = /^#[0-9a-fA-F]{6}$/;
export const CONTEST_BALLOON_NAME_MAX = 40;

export type ContestBalloonStatus = "pending" | "delivered";

/** A balloon as the board returns it: the entry's name, the seat stamped when it was raised, never an email. */
export type ContestBalloon = {
  _id: string;
  entryId: string;
  entryName: string | null;
  problemLetter: string;
  color: ContestBalloonColor | null;
  seatLabel: string | null;
  roomId: string | null;
  roomName: string | null;
  firstAcAt: string;
  firstInContest: boolean;
  status: ContestBalloonStatus;
  deliveredBy: string | null;
  deliveredAt: string | null;
};

/**
 * Self-contained, because a volunteer never reads the organizer detail: the
 * board and the runner page draw themselves from this alone.
 */
export type ContestBalloonBoardResponse = {
  contest: {
    _id: string;
    title_en: string;
    title_bn: string | null;
    participation: ContestParticipationMode;
    balloonsDuringFreeze: boolean;
    freezeAt: string | null;
  };
  problems: { letter: string; problemId: string; color: ContestBalloonColor | null }[];
  rooms: { id: string; name: string }[];
  /** Pending oldest first, then delivered newest first. */
  balloons: ContestBalloon[];
  counts: {
    pending: number;
    delivered: number;
    byProblem: { letter: string; pending: number; delivered: number }[];
  };
};

/* ── The print queue (`EX-ONS02`, `docs/29` §6.2) ────────────────────────── */

export const CONTEST_PRINT_SOURCE_MAX = 100_000;

/** How many slips one standings entry may print: in ten minutes, and in the contest. */
export type PrintLimits = { perEntryPer10Min: number; perEntryPerContest: number };

export type ContestPrintStatus = "queued" | "printing" | "printed" | "delivered" | "cancelled";

/** The moves a station may make, keyed by the status a job moves to. */
export const CONTEST_PRINT_NEXT: Readonly<Record<ContestPrintStatus, ContestPrintStatus | null>> = {
  queued: "printing",
  printing: "printed",
  printed: "delivered",
  delivered: null,
  cancelled: null,
};

/** A slip as every list read returns it: never the source. */
export type ContestPrintJob = {
  _id: string;
  entryId: string;
  entryName: string | null;
  requestedBy: string;
  requestedByName: string | null;
  problemLetter: string;
  seatLabel: string;
  roomId: string;
  roomName: string | null;
  languageKey: string;
  languageVersion: string;
  pages: number;
  status: ContestPrintStatus;
  requestedAt: string;
  updatedAt: string;
};

export type RequestPrintInput = {
  letter: string;
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
};

export type RequestPrintResponse = {
  job: ContestPrintJob;
  limits: PrintLimits;
  used: { window: number; contest: number };
};

export type OwnPrintJobsResponse = { jobs: ContestPrintJob[]; limits: PrintLimits };

export type ContestPrintQueueResponse = {
  jobs: ContestPrintJob[];
  counts: {
    total: number;
    pages: number;
    byEntry: { id: string; name: string | null; jobs: number; pages: number }[];
    byProblem: { letter: string; jobs: number; pages: number }[];
  };
  rooms: { id: string; name: string }[];
  limits: PrintLimits;
  participation: ContestParticipationMode;
};

export type ContestPrintStationResponse = {
  room: { _id: string; name: string };
  /** Queued, printing and printed, oldest first. */
  active: ContestPrintJob[];
  /** The most recently delivered handful. */
  delivered: ContestPrintJob[];
};

export type ContestPrintJobPdf = { fileName: string; pages: number; pdfBase64: string };

export type OrganizerProblemSetItem = {
  letter: string;
  problemId: string;
  slug: string | null;
  title_en: string | null;
  title_bn: string | null;
  difficulty: ProblemDifficulty | null;
  /** A `public` problem in a contest is practisable outside it - surfaced, not refused. */
  visibility: string | null;
  /**
   * `EX-CFS01`: what this problem is worth under the `codeforces` format, on the
   * mount row rather than on the judge problem. `null` only for a set mounted
   * before that task.
   */
  maxScore: number | null;
  /** `EX-ONS03`: the balloon colour on the mount row, `null` where none was set. Optional on the wire. */
  balloonColor?: ContestBalloonColor | null;
  /** `EX-SRC01`: a public problem whose programs a solver can already read. Optional for an older API. */
  solutionsOpen?: boolean;
  /**
   * `EX-SFX02`, ADR-0107: when the problem entered the set, who wrote it,
   * whether the reader may open it in the Author Studio, and what the next save
   * would refuse it for. Optional, because a web deploy can land before the
   * backend's; absent reads as "not known", never as "ready".
   */
  addedAt?: string | null;
  author?: ContestProblemAuthor | null;
  byViewer?: boolean;
  viewerCanAuthor?: boolean;
  blockers?: ContestProblemBlocker[];
  /**
   * `EX-PDF01`: the problem has a Bangla statement, so it can print in Bangla.
   * Optional on the wire: an older backend sends nothing, read as "no".
   */
  hasBanglaStatement?: boolean;
};

/* ── `EX-PDF01` / `EX-PDF02`: the problem set on paper (`docs/29` §6.13) ──── */

export const PROBLEM_SET_PDF_LANGS = ["en", "bn"] as const;
export type ProblemSetPdfLang = (typeof PROBLEM_SET_PDF_LANGS)[number];
export const PROBLEM_SET_PDF_PAPERS = ["a4", "letter"] as const;
export type ProblemSetPdfPaper = (typeof PROBLEM_SET_PDF_PAPERS)[number];

/** One row of `GET .../problem-set/downloads`, newest first (`moderate`). */
export type ContestProblemSetDownload = {
  at: string;
  kind: "set" | "problem";
  letters: string[];
  lang: ProblemSetPdfLang;
  paper: ProblemSetPdfPaper;
  cover: boolean;
  phase: "before_start" | "live" | "after_end";
  surface: "console" | "contest_page";
  role: "owner" | "moderator" | "lead_author" | "author" | "tester" | "org_admin" | "operator" | "reader";
  /** The name stamped on the pages; an operator is `{ name: "Progsity", username: null }`. */
  by: { name: string; username: string | null };
};

export type ContestProblemSetDownloadsResponse = {
  items: ContestProblemSetDownload[];
  total: number;
  page: number;
  limit: number;
};

/**
 * `EX-SFX02`, ADR-0107: one reason the next save would refuse a problem, in
 * the order the save asks them. `not_attachable` is only ever said of a
 * problem new to the set: one already in it is not asked again.
 */
export type ContestProblemBlocker =
  | { reason: "not_attachable" }
  | { reason: "no_expected_time" }
  | {
      reason: "reference_solution_unverified";
      status: "unverified" | "passed" | "failed" | "timed_out";
      /** A pass exists, earned against tests that have changed since. */
      stale: boolean;
      /** 1-based. */
      failedTestIdx?: number;
      verdict?: string;
    }
  | { reason: "no_points" };

/** Who wrote a problem, by name; never an email. */
export type ContestProblemAuthor = { id: string; name: string; username: string | null };

/**
 * One row of `GET .../problems/check` (`EX-SFX02`): what the next save would
 * say about a problem not yet saved into the set. A problem the reader may not
 * add comes back with nothing but that, titles and author `null`.
 */
export type ContestProblemCheckItem = {
  problemId: string;
  inSet: boolean;
  slug: string | null;
  title_en: string | null;
  title_bn: string | null;
  author: ContestProblemAuthor | null;
  byViewer: boolean;
  viewerCanAuthor: boolean;
  blockers: ContestProblemBlocker[];
};

export type ContestProblemCheckResponse = { items: ContestProblemCheckItem[] };

export type OrganizerProblemSetResponse = {
  items: OrganizerProblemSetItem[];
  total: number;
  /** `EX-SRC01`: on a rated round, the letters whose programs are already readable. Never a refusal. */
  warnings?: { letter: string; reason: "solutions_open" }[];
};

export type OrganizerParticipant = {
  accountKind?: "full" | "contest_temp";
  expiresAt?: string | null;
  credentialSheetDownloadedAt?: string | null;
  userId: string;
  name: string | null;
  email: string | null;
  registeredVia: ContestRegistration;
  invitedBy: string | null;
  registeredAt: string;
  status: ContestParticipantStatus;
  statusReason: string | null;
  statusAt: string | null;
  teamId: string | null;
  teamName: string | null;
  /** `EX-RUL01`. `null` for a row written before the acceptance gate existed. */
  acceptedRulesAt: string | null;
};

export type OrganizerParticipantsResponse = {
  items: OrganizerParticipant[];
  total: number;
  /** The badge on the approval queue, counted whatever the caller filtered to. */
  pending: number;
  /** `EX-RUL01`: how many of `total` accepted the rules page, counted server-side. */
  acceptedRulesCount: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type OrganizerTeamMember = {
  userId: string;
  name: string | null;
  email: string | null;
  status: ContestParticipantStatus;
};

/** `EX-SCB01`: one person on a team, as the organizer typed them. */
export type OrganizerTeamRosterEntry = {
  name: string;
  nickname: string | null;
  institution: string | null;
  role: "member" | "coach";
};

export type OrganizerTeam = {
  _id: string;
  name: string;
  status: ContestTeamStatus;
  statusReason: string | null;
  statusAt: string | null;
  createdAt: string;
  /** The accounts that log in and whose submissions the fold charges to the team. */
  members: OrganizerTeamMember[];
  /**
   * `EX-SCB01`: the team's display metadata. `roster` is the people beside
   * (never instead of) `members`: an issued contest has one login per team and
   * three people on the roster. Optional, so a client meeting an older backend
   * reads the team it always read.
   */
  institution?: string | null;
  institutionLogoImageId?: string | null;
  country?: string | null;
  seatLabel?: string | null;
  roster?: OrganizerTeamRosterEntry[];
};

/**
 * `PATCH .../teams/:teamId`, `EX-SCB01`. Every field optional; `{ name }` alone
 * is the rename it always was. `null` clears a field; `roster` replaces the list.
 */
export type UpdateContestTeamInput = {
  name?: string;
  institution?: string | null;
  institutionLogoImageId?: string | null;
  country?: string | null;
  roster?: { name: string; nickname?: string; institution?: string; role: "member" | "coach" }[];
};

export type OrganizerTeamsResponse = {
  items: OrganizerTeam[];
  total: number;
  participation: ContestParticipationMode;
};

/**
 * The queue, with the asker on it - the one difference from the contestant's
 * list that matters: half of answering a question is deciding whether the
 * answer belongs to one team or to the room.
 */
export type OrganizerClarification = Omit<ContestClarification, "mine"> & {
  askedBy: { userId: string; name: string | null; email: string | null };
  team: { _id: string; name: string | null } | null;
  /** `EX-ONS01`: where to carry a paper answer; `null` when unseated, absent on an older backend. */
  seat?: { label: string; room: string } | null;
  /** `EX-CLA01`: the console's group; `null` for a group of one, absent on an older backend. */
  group?: { id: string; title: string; by: "ai" | "staff" } | null;
};

/** `EX-CLA01`: who pays the next Clarification Assistant press. */
export type ClarificationAssistAllowance = {
  included: boolean;
  usesIncluded: number;
  usesLeft: number;
  payer: "included" | "credits";
};

export const CLARIFICATION_DRAFT_KINDS = ["answer", "read_statement", "no_comment", "yes", "no", "unclear"] as const;
export type ClarificationDraftKind = (typeof CLARIFICATION_DRAFT_KINDS)[number];

export type ClarificationStatementSection = "statement" | "input" | "output" | "constraints" | "notes";

/** `EX-CLA01`: a draft answer, returned to the composer and never stored. */
export type ClarificationDraft = {
  kind: ClarificationDraftKind;
  answer: string;
  quote: { section: ClarificationStatementSection; line: number; lineText: string; text: string } | null;
  quoteDropped: boolean;
};

export type ClarificationDraftResponse = {
  letter: string | null;
  questions: number;
  draft: ClarificationDraft;
  payer: "included" | "credits";
};

/* ── The venue (`EX-ONS01`, `docs/29` §6.2) ──────────────────────────────── */

export const CONTEST_ROOM_GRID_MAX = 60;
export const CONTEST_ROOM_LIST_MAX = 500;
export const CONTEST_ROOM_DEFAULT_PATTERN = "{room}-{n:02}";

export type ContestRoomLayout = "grid" | "list";
export type ContestSeatStatus = "free" | "assigned" | "blocked";

export type ContestVenue = { _id: string; name: string; updatedAt?: string };

export type ContestRoom = {
  _id: string;
  name: string;
  layout: ContestRoomLayout;
  rows: number | null;
  cols: number | null;
  labelPattern: string;
  order: number;
};

/** A chair. `entry` is who sits there: a team, or a person in an individual contest. */
export type ContestSeat = {
  _id: string;
  roomId: string;
  label: string;
  row: number | null;
  col: number | null;
  ordinal: number;
  status: ContestSeatStatus;
  entry: { type: "team" | "user"; id: string; name: string | null } | null;
};

export type ContestUnseatedEntry = {
  type: "team" | "user";
  id: string;
  name: string | null;
  institution: string | null;
};

export type ContestVenueResponse = {
  venue: ContestVenue | null;
  rooms: ContestRoom[];
  /** Every seat of every room, in room order then position order. */
  seats: ContestSeat[];
  /** The field's entries with no chair yet. */
  unseated: ContestUnseatedEntry[];
  participation: ContestParticipationMode;
};

export type CreateContestRoomInput =
  | { name: string; layout: "grid"; rows: number; cols: number; labelPattern?: string }
  | { name: string; layout: "list"; labels: string[] };

/** Exactly one of the two, by the contest's participation mode. */
export type AssignContestSeatInput = { teamId: string } | { participantId: string };

export type OrganizerClarificationsResponse = {
  items: OrganizerClarification[];
  total: number;
  pending: number;
  page: number;
  limit: number;
  totalPages: number;
  /** `EX-CLA01`: the Answered tab's count; absent on an older backend. */
  answered?: number;
  /** `EX-CLA01`: absent on an older backend. */
  assist?: ClarificationAssistAllowance;
};

export type OrganizerSubmission = {
  submissionId: string;
  letter: string | null;
  userId: string;
  name: string | null;
  email: string | null;
  languageKey: string;
  languageVersion: string;
  status: "queued" | "running" | "completed";
  verdict: JudgeVerdict | null;
  score: number;
  maxScore: number;
  timeMs: number | null;
  memoryKb: number | null;
  rejudgedAt: string | null;
  rejudgeCount: number;
  createdAt: string;
};

export type OrganizerSubmissionsResponse = {
  items: OrganizerSubmission[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

/** The source code lives here and nowhere else in the module (`docs/29` §6.2). */
export type OrganizerSubmissionDetail = {
  submissionId: string;
  letter: string | null;
  problem: { _id: string; slug: string; title_en: string } | null;
  contestant: { userId: string; name: string | null; email: string | null };
  /**
   * `EX-WSP01`: what the snapshot read is addressed by. Optional because a
   * backend before this deploy does not send them; the entry id is the team in
   * a team contest and the person otherwise.
   */
  problemId?: string;
  teamId?: string | null;
  participantId?: string;
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
  createdAt: string;
  rejudgedAt: string | null;
  rejudgeCount: number;
  status: "queued" | "running" | "completed";
  verdict?: JudgeVerdict;
  score: number;
  maxScore: number;
  timeMs?: number;
  memoryKb?: number;
  compileOutput?: string;
  /** The grader's view, policy `full` - they decide whether the test data was wrong. */
  testResults?: StudentTestResult[];
};

export type OrganizerSubmissionsQuery = {
  letter?: string;
  userId?: string;
  /** `EX-SCB05`: a team row's attempts, every member's (the route resolves the members). */
  teamId?: string;
  verdict?: JudgeVerdict;
  status?: "queued" | "running" | "completed";
  page?: number;
  limit?: number;
};

export type RejudgeInput = {
  submissionIds?: string[];
  letter?: string;
  verdict?: JudgeVerdict;
  all?: true;
};

export type RejudgeResult = {
  requeued: number;
  selected: number;
  /** "3 of 5 - two were already in the queue", rather than a number nobody asked for. */
  skipped: number;
};

/* ── Rating (`EX-PUB07`, `docs/29` §7) ────────────────────────────────────── */

export type ContestRatingEntry = StandingsRowRating & {
  userId: string;
  name: string | null;
  rank: number;
};

/**
 * The ledger read, and it answers in three states rather than 404: "not rated",
 * "rated, not applied yet" and "here they are" are different things to tell
 * somebody refreshing an hour after the finish.
 */
export type ContestRatingResponse = {
  /** `EX-DIV01`: `division` is the result's division, `null` when Open; optional for an older API. */
  contest: { slug: string; title_en: string; phase: ContestPhase; division?: ContestDivision | null };
  rated: boolean;
  applied: boolean;
  appliedAt: string | null;
  items: ContestRatingEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type RatingApplyResult = {
  contestId: string;
  slug: string;
  appliedAt: string;
  reapplied: boolean;
  participants: number;
  /** Ledger rows dropped because those contestants are no longer in the field. */
  removed: number;
  /** Zero, always. It is the invariant the whole design is built around. */
  sumOfDeltas: number;
};

export type RatingReplayResult = {
  from: string;
  appliedAt: string;
  replayed: {
    contestId: string;
    slug: string;
    endAt: string | null;
    participants: number;
    removed: number;
    sumOfDeltas: number;
  }[];
  /** Contests the bound did not reach. Reported rather than swallowed. */
  remaining: number;
  limit: number;
};

export type ParticipantStatusResult = {
  changed: boolean;
  userId: string;
  status: ContestParticipantStatus;
  previousStatus: ContestParticipantStatus;
};

/* ── `EX-TST01`: the staff rehearsal (`docs/29` §4.1) ─────────────────────── */

/**
 * One staff run before the round, as the rehearsal list returns it. Never the
 * source; the per-test detail is {@link ContestRehearsalDetail}.
 */
export type ContestRehearsalItem = {
  submissionId: string;
  name: string | null;
  /** Read at request time: somebody taken off staff since is `null`. */
  role: ContestStaffRole | null;
  mine: boolean;
  letter: string | null;
  languageKey: string;
  languageVersion: string;
  status: "queued" | "running" | "completed";
  verdict?: JudgeVerdict;
  score: number;
  maxScore: number;
  timeMs?: number;
  memoryKb?: number;
  passedCount: number;
  totalCount: number;
  /** The first test that was neither accepted nor skipped, or `null`. */
  failedTest: number | null;
  createdAt: string;
};

export type ContestRehearsalsResponse = {
  phase: ContestPhase;
  /** Whether this reader may still rehearse: staff, before the start. */
  canRehearse: boolean;
  items: ContestRehearsalItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

/** One rehearsal with every test's verdict and time (the `full` policy), never test data. */
export type ContestRehearsalDetail = Omit<
  ContestRehearsalItem,
  "passedCount" | "totalCount" | "failedTest"
> & {
  compileOutput?: string;
  testResults?: StudentTestResult[];
};

/** A submission still awaiting a verdict - drives the contest workspace's poll. */
export function isContestSubmissionPending(
  submission: Pick<ContestSubmissionItem, "status">,
): boolean {
  return submission.status !== "completed";
}

/* ── `EX-ANM01`: the solve-time report (`docs/29` §6.2, §11 decision 16) ───── */

export type TimingSignalKind = "first_ac_too_fast" | "gap_too_short" | "set_too_fast";
export type TimingBaseline = "author" | "field_median";

export type TimingSignal = {
  kind: TimingSignalKind;
  problemId: string | null;
  letter: string | null;
  observedMinutes: number;
  thresholdMinutes: number;
  expectedMinutes: number;
  /** Which clock the threshold came from. `field_median` is the weaker of the two. */
  baseline: TimingBaseline;
};

export type TimingProblemSummary = {
  letter: string;
  problemId: string;
  /** `null` when there is no expectation at all: no author value, too few solves. */
  expectedMinutes: number | null;
  baseline: TimingBaseline | null;
  medianMinutes: number | null;
  p10Minutes: number | null;
  solvedCount: number;
  underThreshold: { firstAc: number; gapInto: number };
};

export type TimingEntryTimeline = {
  entryId: string;
  entryType: "user" | "team";
  solves: { letter: string; problemId: string; minute: number }[];
  signals: TimingSignal[];
  /** When the stored record was last written, or `null` if no run has written one. */
  recordedAt: string | null;
  /**
   * `EX-TIM01`: the team's name in a team contest, the person's name otherwise;
   * never an email. `null` when unknown, absent from a backend before the field.
   */
  name?: string | null;
};

/**
 * `EX-RDY01` (`docs/29` §6.14): the Overview's "Ready for the day", one server
 * read. Nothing in it refuses Schedule. `reason` and `params` are stable keys
 * the console translates; `fix` names the page that fixes the line.
 */
export type ReadinessCheckStatus = "pass" | "fail" | "warn" | "todo" | "none";
export type ReadinessStudioSection = "solutions" | "validator" | "statement" | "settings";
export type ReadinessFix = { studio: ReadinessStudioSection } | { console: "overview" | "problems" | "staff" | "venue" | "print" | "balloons" };
export type ReadinessCheck = {
  status: ReadinessCheckStatus;
  reason?: string;
  params?: Record<string, string | number>;
  fix?: ReadinessFix;
};
export type ReadinessProblemColumn = "main" | "wrong" | "tester" | "validator" | "time" | "bangla";
export type ContestReadinessProblem = Record<ReadinessProblemColumn, ReadinessCheck> & {
  problemId: string;
  letter: string;
  title_en: string;
  canOpen: boolean;
};
export type ContestReadinessLineKey = "balloons" | "staff" | "problem_set_pdf" | "seats" | "print" | "warm_up" | "judge_size";
export type ContestReadiness = {
  checkedAt: string;
  problems: ContestReadinessProblem[];
  round: (ReadinessCheck & { key: ContestReadinessLineKey })[];
  summary: { done: number; total: number };
};

export type ContestTimingReport = {
  problems: TimingProblemSummary[];
  entries: TimingEntryTimeline[];
  /**
   * `EX-PKG02`: which half of the report this contest's capability stamp bought
   * (`docs/33` §5.5). `summary` means `entries` is empty because it was not
   * served, never because the detectors cleared the field.
   */
  timingReport: "summary" | "full";
  /** `set_too_fast` cannot fire on this contest: a problem has no clock, or the set is too small. */
  setSignalUnavailable: boolean;
  detectorVersion: number;
  storedAt: string | null;
};

/* ── `EX-AUT06`: the cluster review and the integrity record (`docs/29` §6.2) ── */

/**
 * One person inside a similarity cluster.
 *
 * The authorship half is nullable throughout, and `null` means **unmeasured**
 * rather than zero (`docs/27` §5.5.2): a contest whose profile never enabled
 * authorship shows a column of blanks, which is the true answer and not an empty
 * one. No field here carries a line of anybody's program.
 */
export type ContestClusterMember = {
  userId: string;
  name: string | null;
  submissionId: string | null;
  /** The standings entry id, which is what the record route takes. */
  participantId: string | null;
  pasteBytes: number | null;
  largestPasteBytes: number | null;
  /** `EX-EDR03`: bytes that arrived as an uploaded file; shown, never in `pastedShare`. */
  uploadBytes?: number | null;
  typedShare: number | null;
  pastedShare: number | null;
  /** Triage only, and the entry's, because that is what the score is of. */
  riskScore: number | null;
};

export type ContestSimilarityCluster = {
  /** Stable across runs, derived from the membership. Links to the record. */
  clusterId: string;
  members: ContestClusterMember[];
  topScore: number;
  exactEdges: number;
  edges: { left: string; right: string; score: number; exact: boolean }[];
};

/**
 * One problem's clusters, with the three counts that keep the answer honest.
 *
 * "Nobody in this room resembles anybody" reads very differently next to "and
 * nothing was ever compared" (`docs/27` §9.19.6), so both are shown.
 */
export type ContestSimilarityGroup = {
  letter: string | null;
  problemId: string;
  comparedCount: number;
  notComparableCount: number;
  notComparedCount: number;
  /**
   * `EX-GAT01`: how many groups the run found, on every tier. On a `summary`
   * report this is the number beside an empty `clusters`. Optional on the wire
   * until every backend serves it.
   */
  clusterCount?: number;
  clusters: ContestSimilarityCluster[];
};

export type ContestSimilarityReport = {
  items: ContestSimilarityGroup[];
  total: number;
  /**
   * `EX-GAT01`: the level served, named after the stamp field. `summary` means
   * the groups were withheld, not that none were found. Absent from an older
   * backend, which served everything.
   */
  similarityReport?: "summary" | "full";
};

/* ── `EX-CMP01`: two programs side by side (`docs/29` §6.2) ─────────────── */

/** One side of a matched region: where it sits in that program. */
export type ContestMatchedRegionSide = {
  /** Character offsets into the program, `[start, end)`. */
  start: number;
  end: number;
  /** 1-based, inclusive. */
  startLine: number;
  endLine: number;
  /** Names renamed inside the region, as `[start, end)` offsets. */
  renamed: [number, number][];
};

/** A passage the two programs share once names, layout and comments are set aside. */
export type ContestMatchedRegion = {
  tokens: number;
  left: ContestMatchedRegionSide;
  right: ContestMatchedRegionSide;
  /** Each renaming once: `left` on the left program stands where `right` is on the right one. */
  renames: { left: string; right: string }[];
};

/** One program of the pair, with its verdict, time and the authorship facts on that problem. */
export type ContestPairSide = {
  userId: string;
  name: string | null;
  submissionId: string;
  languageKey: string;
  languageVersion: string | null;
  sourceCode: string;
  submittedAt: string;
  status: "queued" | "running" | "completed";
  verdict: JudgeVerdict | null;
  summary: { tokenCount: number; lineCount: number; matchedTokens: number; matchedShare: number };
  participantId: string | null;
  pasteBytes: number | null;
  largestPasteBytes: number | null;
  uploadBytes: number | null;
  typedShare: number | null;
  pastedShare: number | null;
  riskScore: number | null;
};

export type ContestSimilarityPairView = {
  problemId: string;
  letter: string;
  problemTitle: string | null;
  contestStartAt: string | null;
  artefact: "code" | "text";
  /** The engine's estimate. `null` when the two were never scored against each other. */
  score: number | null;
  exact: boolean;
  comparedAt: string | null;
  left: ContestPairSide;
  right: ContestPairSide;
  /** In reading order on the left program. */
  regions: ContestMatchedRegion[];
  truncated: boolean;
};

/** `pair` is `null` on a `summary` report: who resembles whom is the withheld half. */
export type ContestSimilarityPairReport = {
  similarityReport: "summary" | "full";
  pair: ContestSimilarityPairView | null;
};

/** A pair as a cluster's edge names it. */
export type ContestSimilarityPairKey = { problemId: string; left: string; right: string };

export type ContestRiskSignal = {
  type: string;
  /** Every occurrence, including the ones beyond the counted cap. */
  count: number;
  counted: number;
  weight: number;
  points: number;
};

export type ContestArtefactRow = {
  problemId: string;
  letter: string | null;
  ownerId: string;
  submissionId: string | null;
  pasteBytes: number | null;
  largestPasteBytes: number | null;
  /** `EX-EDR03`: bytes that arrived as an uploaded file; shown, never in `pastedShare`. */
  uploadBytes?: number | null;
  sourceBytes: number;
  typedShare: number | null;
  pastedShare: number | null;
  similarityScore: number | null;
  similarityExact: boolean;
  signals: string[];
};

export type ContestIntegrityRecord = {
  /**
   * `EX-INT06`: the level served, the whole-round report's word. `summary`
   * means `live` and `stored` are withheld by the stamp, not empty. Absent
   * from an older backend, which served everything.
   */
  integrityReport?: "summary" | "full";
  participantId: string;
  entryType: "user" | "team";
  members: { userId: string; name: string | null }[];
  /**
   * What was watched, and what was not. Every `false` here is a sentence the
   * panel has to render: an empty list on a contest nothing was watching proves
   * nothing, and showing it without saying so is misleading by omission
   * (`docs/27` §4.1, §9.17.6).
   */
  watched: {
    authorship: boolean;
    similarity: boolean;
    /** Which meter produced the paste numbers. `null` when there are none. */
    pasteSource: "server" | "client" | null;
    timingSignals: number;
  };
  live: {
    authorship: {
      submissions: number;
      pasteBytes: number;
      largestPasteBytes: number;
      /** `EX-EDR03`: absent reads as zero. */
      uploadBytes?: number;
      typedRatio?: number;
      source: "server" | "client";
    } | null;
    similarity: {
      clusterIds: string[];
      maxScore?: number;
      comparedProblems: number;
    } | null;
    risk: {
      score: number;
      capped: boolean;
      signals: ContestRiskSignal[];
      totalEvents: number;
    } | null;
    artefacts: ContestArtefactRow[];
  } | null;
  stored: {
    authorship: unknown | null;
    similarity: unknown | null;
    risk: { score: number; capped: boolean; version: number; computedAt: string } | null;
    serverSignals: TimingSignal[];
    timing: { computedAt: string; detectorVersion: number } | null;
  } | null;
  /** Read-only: the sanction a person took, on the participants panel. */
  sanction: {
    status: string;
    reason: string | null;
    decidedBy: string | null;
    decidedAt: string | null;
  } | null;
};

/**
 * One row of the organizer's whole-round integrity report (`EX-INT04`).
 *
 * Counts and bytes. No source, no hash, no address, and nothing that is a rank
 * or a penalty: a signal is evidence for a person to read (`docs/27` §5.5,
 * ADR-0019).
 */
export type ContestIntegrityReportRow = {
  participantId: string;
  entryType: "user" | "team";
  members: { userId: string; name: string | null }[];
  windowExits: number;
  fullscreenExits: number;
  /**
   * Seconds this browser was silent, from the server's own `presence_gap`
   * measurements. **Not "seconds away from the page"**: nothing reports a
   * return, so a duration derived from tab switches would be a guess with a unit
   * on it (`docs/27` §4.1).
   */
  silentSeconds: number;
  pasteBytes: number | null;
  largestPasteBytes: number | null;
  /** `EX-EDR03`: bytes that arrived as an uploaded file, `null` when unmeasured. */
  uploadBytes?: number | null;
  pasteSource: "server" | "client" | null;
  signals: {
    presenceGap: number;
    telemetryGap: number;
    sessionConflict: number;
    /** `EX-EDO01`: optional, a backend before the level does not send them. */
    networkChange?: number;
    deviceChange?: number;
    pasteRefused?: number;
    holds?: number;
  };
  timingSignals: number;
  risk: { score: number; capped: boolean } | null;
  eventsRecorded: number;
  eventsShown: number;
  /*
   * `EX-INT07`, each optional until every backend serves it. `measured: false`
   * is "the page sent nothing", never "nothing happened".
   */
  measured?: boolean;
  rank?: number | null;
  solved?: number;
  marks?: ContestIntegrityMark[];
  marksTruncated?: boolean;
  solves?: { letter: string; atMs: number }[];
};

/** `EX-INT07`: one recorded moment as a report row carries it, for its strip and its sentence. */
export type ContestIntegrityMark = {
  /** The event's type, or `statement_copy` for a statement try (`EX-STP01`). */
  kind: string;
  /** Milliseconds from the window's start. */
  atMs: number;
  /** How long it lasted where something measured it, else `null`. */
  durationMs: number | null;
  letter: string | null;
  bytes: number | null;
};

/** `EX-INT07`: one moment on an entry's timeline. */
export type ContestIntegrityMoment = ContestIntegrityMark & {
  type: string;
  at: string;
  /** `page` is the contestant's clock, `server` the server's own measurement. */
  durationSource: "page" | "server" | null;
  detail: string | null;
  lines: number | null;
  serverDetected: boolean;
  userId: string;
};

/** `EX-INT07`: `GET .../integrity/:participantId/timeline`. */
export type ContestIntegrityTimeline = {
  integrityReport: "summary" | "full";
  participantId: string;
  entryType: "user" | "team";
  teamName: string | null;
  members: { userId: string; name: string | null }[];
  window: { startAt: string | null; endAt: string | null };
  /** `null`, with the two counts, on a `summary` stamp. */
  measured: boolean | null;
  eventsRecorded: number | null;
  eventsShown: number | null;
  moments: ContestIntegrityMoment[];
  solves: { at: string; atMs: number; letter: string; userId: string }[];
};

/**
 * The report. **The header is the report** (`docs/27` §4.1): a list of counts
 * with no statement of what was watched reads as "this contestant did nothing"
 * when it means "nobody was looking".
 *
 * `entries` is empty on a plan without the per-contestant report; the header and
 * the totals still serve, because refusing outright would take the free half
 * with it (`docs/33` §5.5).
 */
export type ContestIntegrityReport = {
  integrityReport: "summary" | "full";
  /** `EX-INT07`: the window the strips span. Optional until every backend serves it. */
  window?: { startAt: string | null; endAt: string | null };
  watched: {
    profile: ContestIntegrityProfile;
    axes: string[];
    tabSwitch: "off" | "tracked" | "warn";
    fullscreen: "off" | "required";
    clipboard: "off" | "block";
    similarity: boolean;
    screenCapture: "not_available";
    webcam: "never";
    firstSeenAt: string | null;
  };
  totals: {
    entries: number;
    entriesWithEvents: number;
    windowExits: number;
    signals: number;
  };
  entries: ContestIntegrityReportRow[];
};

/** One event on the wire, in the proctoring module's own vocabulary. */
export type ContestTelemetryEvent = {
  type: string;
  detail?: string;
  /** The proctoring module's word for the item; a contest's item is a letter. */
  itemId?: string;
  timestamp: string;
  seq: number;
  prevHash: string | null;
  paste?: { bytes: number; lines: number };
  /** `EX-INT07`: on a `page_return` only, how long the page was left. */
  awayMs?: number;
};

/* ── The Round Report (`EX-RPT01`, `docs/29` §6.15) ──────────────────────────── */

export type ReportVerdict =
  | "accepted"
  | "wrong_answer"
  | "time_limit_exceeded"
  | "memory_limit_exceeded"
  | "runtime_error"
  | "compilation_error"
  | "presentation_error"
  | "internal_error"
  | "skipped";

export type ContestReportSummary = {
  /** `draft` is the AI's, unseen outside the console; `accepted` is a moderator's Save. */
  status: "draft" | "accepted";
  en: string;
  bn: string;
  includeInPdf: boolean;
  author: "ai" | "person";
  /** The numbers moved (a rejudge) since it was written. */
  stale: boolean;
  writtenAt: string;
  acceptedAt: string | null;
  acceptedBy: string | null;
};

export type ContestReport = {
  contest: {
    id: string;
    slug: string;
    title_en: string;
    phase: ContestPhase;
    participation: "individual" | "team";
    scoringFormat: string;
    startAt: string | null;
    endAt: string | null;
    freezeAt: string | null;
  };
  /** False while the board is frozen: every number is the frozen board's. */
  final: boolean;
  cap: string | null;
  windowMinutes: number;
  freezeMinute: number | null;
  totals: {
    registered: number;
    tookPart: number;
    submissions: number;
    accepted: number;
    problemCount: number;
    problemsSolved: number;
    firstAccepted: { minute: number; letter: string } | null;
  };
  problems: {
    letter: string;
    title_en: string;
    solved: number;
    tried: number;
    firstSolveMinute: number | null;
    solveMinutes: number[];
  }[];
  verdicts: { verdict: ReportVerdict; count: number }[];
  languages: { languageKey: string; name: string; count: number }[];
  institutions: { name: string; entries: number; bestRank: number | null; bestSolved: number }[];
  stuck: { letter: string; test: number; verdict: ReportVerdict; entries: number }[];
  topSolved: number[];
  integrity: { comparisonGroups: number; timingFlags: number; disqualified: number };
  written: {
    /** The stamp's `writtenReport`. */
    included: boolean;
    /** Ended and unfrozen: the summary and the PDF may be made. */
    open: boolean;
    writesLeft: number;
    writesIncluded: number;
    summary: ContestReportSummary | null;
  };
};

/** `EX-OVL01`: the live stream overlay's six layers, each one OBS browser source. */
export const LIVE_LAYERS = ["ticker", "alerts", "clock", "bars", "lower", "scene"] as const;
export type LiveLayer = (typeof LIVE_LAYERS)[number];

/** `EX-OVL01`: the console's Broadcast section, `GET .../broadcast`. */
export type ContestBroadcast = {
  /** The stamp's `liveOverlay`. */
  included: boolean;
  /** Every layer's link carries it; `null` until the first press. */
  key: string | null;
  layers: Record<LiveLayer, boolean>;
  keyMadeAt: string | null;
  refreshSeconds: number;
};

/**
 * `EX-CLI01`: the console's ICPC feed card, `GET .../feed`. The token itself is
 * never in this shape: only in the one answer that makes it ({@link ContestFeedMade}).
 */
export type ContestFeed = {
  /** The stamp's `icpcFeed`. */
  included: boolean;
  /** False on a Codeforces-format round, which the feed does not serve. */
  supported: boolean;
  /** The contest's id on the feed, given to the Resolver and the CDS with the address. */
  clicsId: string;
  token: { hint: string; madeAt: string; lastUsedAt: string | null } | null;
  revokedAt: string | null;
  finalResults: boolean;
  finalResultsAt: string | null;
};

/** The answer to "Make a token": the card, and the token, shown once. */
export type ContestFeedMade = ContestFeed & { secret: string };

export type LiveEntry = { name: string | null; institution: string | null };
export type LiveSolve = LiveEntry & {
  letter: string;
  minute: number;
  first: boolean;
  /** The problem's balloon; absent from a backend older than the field. */
  balloonColor?: { hex: string; name: string } | null;
};

/** `EX-OVL01`: one layer, `GET /v1/contest-live/:key/:layer`. Each part only when its layer is on. */
export type LiveLayerPayload = {
  layer: LiveLayer;
  on: boolean;
  refreshSeconds: number;
  serverNow: string;
  phase: ContestPhase;
  contest: {
    title_en: string;
    title_bn: string | null;
    scoringFormat: ContestScoringFormat;
    participation: "individual" | "team";
  };
  frozen: boolean;
  clock?: { startAt: string | null; endAt: string | null; freezeAt: string | null };
  top?: (LiveEntry & { rank: number; solved: number; penaltyMinutes: number; score: number })[];
  solves?: LiveSolve[];
  firstSolves?: LiveSolve[];
  problems?: { letter: string; solved: number; tried: number; balloonColor: { hex: string; name: string } | null }[];
  field?: number;
  brand?: {
    organizer: { name_en: string; name_bn: string | null; logoImageId: string | null } | null;
    /** `EX-SPN01`: title sponsors first; absent from a backend older than the field. */
    sponsors?: { name: string; logoImageId: string | null; tier: ContestSponsorTier }[];
  };
};

/** `EX-SPN01`, `docs/29` §6.23: a title sponsor is printed first and larger; a partner after it. */
export const CONTEST_SPONSOR_TIERS = ["title", "partner"] as const;
export type ContestSponsorTier = (typeof CONTEST_SPONSOR_TIERS)[number];
/** The six places the organizer switches the sponsors on, in the canvas's order. */
export const CONTEST_BRAND_PLACES = ["contestPage", "standings", "bigScreen", "certificates", "overlay", "report"] as const;
export type ContestBrandPlace = (typeof CONTEST_BRAND_PLACES)[number];
/** The server's bounds (`Contest.model.ts`). */
export const CONTEST_SPONSORS_MAX = 6;
export const CONTEST_SPONSOR_NAME_MAX = 80;
export const CONTEST_SPONSOR_URL_MAX = 300;

export type ContestSponsor = {
  name: string;
  logoImageId: string | null;
  /** An `http(s)` address, opened with `rel="noopener sponsored"`. */
  url: string | null;
  tier: ContestSponsorTier;
};

/** `EX-SPN01`: the console's "Sponsors and look", `GET .../brand`. */
export type ContestBrandRead = {
  /** The stamp's `liveOverlay`: the sponsors ride with the overlay (Wave 34 decision 8). */
  included: boolean;
  brand: {
    sponsors: ContestSponsor[];
    places: Record<ContestBrandPlace, boolean>;
    accentColor: string | null;
    coverImageId: string | null;
  };
  updatedAt: string | null;
  /**
   * `EX-TAL01` (`docs/29` §6.26): each sponsor row's Progsity Hire company,
   * by the row's index, and how many contestants have ticked the Talent Pass.
   * Optional: a server before it sends none.
   */
  talent?: ContestTalentConsole;
};

/** `EX-TAL01`: a sponsor row's link to a Progsity Hire company. */
export type SponsorCompanyLink = {
  index: number;
  company: { id: string; name: string; code: string; verified: boolean } | null;
};

export type ContestTalentConsole = {
  links: SponsorCompanyLink[];
  /** People whose consent stands now. A count; the console never names them. */
  given: number;
};

export type ContestBrandInput = Omit<ContestBrandRead["brand"], "sponsors"> & {
  sponsors: (ContestSponsor & {
    /** `EX-TAL01`: the Hire company's sponsor code; `null` unlinks, absent keeps the link. */
    hireCompany?: string | null;
  })[];
};

/** `EX-TAL01`: the window a company reads in: after the final board, for 180 days. */
export type TalentPassWindowState = "not_ended" | "frozen" | "open" | "closed";

/** `EX-TAL01`, `GET /v1/contests/:slug/talent-pass`: the reader's own Talent Pass. */
export type MyTalentPass = {
  offered: boolean;
  /** The companies a tick now would name. */
  companies: { id: string; name: string }[];
  consent: {
    state: "given" | "withdrawn";
    givenAt: string;
    withdrawnAt: string | null;
    companyIds: string[];
    /** Whether it names every company the page names now. */
    coversAll: boolean;
  } | null;
  canGive: boolean;
  window: { state: TalentPassWindowState; closesAt: string | null };
};

/** `EX-SPN01`: the contest page's block, `null` without the stamp or with nothing set. */
export type PublicContestBrand = {
  accentColor: string | null;
  coverImageId: string | null;
  /** Title sponsors first. */
  sponsors: ContestSponsor[];
  show: { contestPage: boolean; standings: boolean; bigScreen: boolean };
};

/** `EX-SPN01`: the sheet an organizer hands each sponsor after the round, `GET .../sponsor-sheet`. */
export type ContestSponsorSheet = {
  contest: {
    id: string;
    slug: string;
    title_en: string;
    title_bn: string | null;
    startAt: string | null;
    endAt: string | null;
    participation: "individual" | "team";
    organizer: { name_en: string; name_bn: string | null; logoImageId: string | null } | null;
  };
  sponsors: ContestSponsor[];
  places: Record<ContestBrandPlace, boolean>;
  numbers: {
    pageViews: number;
    pageViewsSince: string | null;
    registered: number;
    tookPart: number;
    institutionCount: number;
    institutions: { name: string; entries: number }[];
  };
  generatedAt: string;
};

/* ── `EX-INS01`: institutions and the season (`docs/29` §6.25) ───────────── */

/** A published season this round is in, for the contest page's Season tab. */
export type ContestPageSeason = {
  slug: string;
  title_en: string;
  title_bn: string | null;
  /** This round's place in the season's order, from 1. */
  round: number;
  rounds: number;
};

/** `GET /v1/contests/:slug/institutions`: the board a contestant reads, regrouped. */
export type ContestInstitutions = {
  contest: { slug: string; title_en: string; title_bn: string | null };
  participation: "individual" | "team";
  frozen: boolean;
  cap: string | null;
  entries: number;
  rows: {
    rank: number;
    institution: string;
    best: { entryId: string; name: string | null; place: number };
    entries: number;
    solved: number;
    /** Its entries' places in the top three, best first. */
    medals: number[];
  }[];
  champion: { name: string | null; institution: string | null; solved: number } | null;
  mostFirstSolves: { name: string | null; institution: string | null; count: number } | null;
  withoutInstitution: number;
};

export type SeasonRoundStatus = "upcoming" | "frozen" | "final";
export type SeasonRoundRefusal =
  | "not_found"
  | "draft"
  | "private"
  | "warm_up"
  | "participation"
  | "capability_not_included";

export type SeasonPoints = { table: number[]; thenMinus: number };

export type SeasonRound = {
  contestId: string;
  label_en: string | null;
  label_bn: string | null;
  slug: string | null;
  title_en: string | null;
  title_bn: string | null;
  startAt: string | null;
  endAt: string | null;
  /** `null` on a round the editor lists that adds nothing. */
  status: SeasonRoundStatus | null;
  refusal: SeasonRoundRefusal | null;
};

export type SeasonCell = { place: number; points: number; counted: boolean };

export type SeasonRow = {
  rank: number;
  name: string | null;
  institution: string | null;
  /** One per round, lined up with `rounds`. */
  cells: (SeasonCell | null)[];
  total: number;
};

export type SeasonHead = {
  slug: string;
  title_en: string;
  title_bn: string | null;
  description_en: string | null;
  description_bn: string | null;
  participation: "individual" | "team";
  points: SeasonPoints;
  bestOf: number | null;
  published: boolean;
  /** An operator's season: Progsity's own. */
  progsity: boolean;
  updatedAt: string;
};

/** `GET /v1/contest-seasons/:slug`: a published season and its table. */
export type PublicSeason = SeasonHead & {
  rounds: SeasonRound[];
  rows: SeasonRow[];
  /** A round in it is still frozen, so its places may move at the unfreeze. */
  provisional: boolean;
};

/** The editor's read: every stored round, with why one adds nothing. */
export type EditorSeason = PublicSeason & { id: string };

export type SeasonListItem = SeasonHead & { id: string; rounds: number };

/** `GET .../contests/:id/seasons`: the console's Season section. */
export type ContestSeasons = {
  /** The stamp's `institutionTable`. */
  included: boolean;
  participation: "individual" | "team";
  seasons: (SeasonListItem & { round: number; editable: boolean })[];
};

export type SeasonInput = {
  title_en: string;
  title_bn: string | null;
  description_en: string | null;
  description_bn: string | null;
  participation: "individual" | "team";
  rounds: { contestId: string; label_en: string | null; label_bn: string | null }[];
  points: SeasonPoints;
  bestOf: number | null;
  published: boolean;
};
